/* Find your fit — quiz flow prototype (Pernille's v3 draft). TEST ONLY.
   Deterministic sizing (underband decides), dot-product ranking, honest empty states. */
(function () {
  if (customElements.get('fit-quiz')) return;
  const IN_TO_CM = 2.54;

  function findRange(ranges, v, tol) {
    for (let i = 0; i < ranges.length; i++) if (v >= ranges[i][0] && v <= ranges[i][1]) return i;
    let best = -1, d0 = Infinity;
    for (let i = 0; i < ranges.length; i++) {
      const d = v < ranges[i][0] ? ranges[i][0] - v : v - ranges[i][1];
      if (d < d0) { d0 = d; best = i; }
    }
    return d0 <= tol ? best : -1;
  }

  // Between-sizes rule (Alexander + Ida, 2026-09-04). PORTED VERBATIM from
  // assets/size-calculator-bra.js so the quiz and the PDP size finder cannot diverge —
  // the quiz previously had its own simpler version that returned NO size whenever the
  // bust landed on an empty cell, where the size finder snaps and explains.
  //
  //   - the underband decides the row, gaps between rows included (tol, 1.5cm: bands are
  //     elastic, per Ida)
  //   - if the bust falls in a column gap or on an empty cell, snap to the nearest FILLED
  //     column on that row, up to BUST_SNAP_CM, and flag it as between-sizes
  //   - beyond that, or an underband truly off the chart: no size. A genuinely
  //     out-of-range bust must never be talked into one.
  //
  // 1.5cm and not the 2.5 originally proposed, per Ida: a column step is about a whole
  // grading, so a bigger jump risks recommending a size too small or too large.
  const BUST_SNAP_CM = 1.5;

  function resolveBandcup(b, ubCm, bustCm, tol, region) {
    const r = { size: null, eu: null, us: null, reason: null, between: false, approx: false };
    const inside = (ranges, v) => ranges.some(x => v >= x[0] && v <= x[1]);
    const ub = findRange(b.underband_cm, ubCm, tol);
    if (ub < 0) { r.reason = 'underband_out'; return r; }
    if (!inside(b.underband_cm, ubCm)) r.between = true;
    const row = b.eu[ub] || [];
    let bu = findRange(b.bust_cm, bustCm, tol);
    if (bu < 0) { r.reason = 'bust_out'; return r; }
    if (!(row[bu] || '').trim()) {
      // Her bust lands on an empty cell — one of the chart's two corners, small band with
      // a large bust or the reverse. The fit team's rule (Ida, 2026-09-18):
      //   "Vi hopper kun en størrelse - enten op eller ned ud fra skemaet - ikke til siderne"
      //   "underband skal lead the way - så vi skal så tæt på at det matcher som muligt
      //    - så må cup følge med"
      // So the underband ROW is fixed and the cup may move ONE step, no further. Not
      // sister sizing: we do not change the band to rescue the cup, even though 70C and
      // 75B are the same cup volume, because the band is the part that has to fit.
      //
      // Measured in CUP STEPS, not centimetres. The old test was bestDist > 1.5cm, which
      // is a different question — the columns are 2.5-4cm wide, so a centimetre threshold
      // allowed or refused a hop depending on where in its column she happened to fall.
      let best = -1;
      if ((row[bu - 1] || '').trim()) best = bu - 1;
      else if ((row[bu + 1] || '').trim()) best = bu + 1;
      if (best < 0) { r.reason = 'combo_out'; return r; }
      bu = best;
      // Not "between sizes" — this is the nearest thing we make, and the customer is
      // told so. Kept separate so the copy can be honest about which it is.
      r.approx = true;
    } else if (!inside(b.bust_cm, bustCm)) {
      r.between = true;
    }
    r.eu = (row[bu] || '').trim() || null;
    r.us = (b.us && b.us[ub] && b.us[ub][bu] || '').trim() || null;
    // DK and EU sell 70C; UK and US sell 34C (Alexander, 2026-09-14)
    r.size = (region === 'us' && r.us) ? r.us : r.eu;
    return r;
  }

  function resolveSizes(rules, ubCm, bustCm, region) {
    const tol = rules.tol || 1.5;
    const out = { alpha: null, between: false, approx: false, bandcup: null };
    const a = rules.alpha;
    const ub = findRange(a.underband_cm, ubCm, tol);
    // Where the bust falls BETWEEN two sizes, the fit team allocated each gap explicitly
    // rather than leaving it to nearest-distance (Ida, 2026-09-18). bust_bands_cm is that
    // allocation, contiguous by construction, so there is no tie to break. Nearest-distance
    // happened to agree almost everywhere and disagreed at three boundaries — 84.4, 89.5
    // and 114.9 — which is the kind of near-agreement that drifts silently the next time
    // somebody changes a tolerance. Their table decides; the old path only falls back.
    const bands = a.bust_bands_cm;
    const bu = bands ? findRange(bands, bustCm, 0)
             : (a.bust_cm ? findRange(a.bust_cm, bustCm, tol) : -1);
    // The underband decides the size (D-015). A bust in a different size says
    // "between sizes" but never changes the recommendation.
    if (ub >= 0) { out.alpha = a.sizes[ub]; out.between = bu >= 0 && bu !== ub; }
    const bc = resolveBandcup(rules.bandcup, ubCm, bustCm, tol, region);
    out.bandcup = bc.size;
    if (bc.between) out.between = true;
    // The chart had no cell for her, so this is the nearest we make rather than a match.
    // Carried separately from `between` because the honest sentence is a different one.
    out.approx = !!bc.approx;
    return out;
  }

  function fitLine(f, t, T) {
    if (!f || !(f.reviews >= t.min_reviews)) return '';
    const v = s => s == null ? null : s <= t.small ? 'small' : s >= t.large ? 'large' : 'true';
    const band = v(f.underband), cup = v(f.cup);
    let overall = v(f.fit != null ? f.fit : f.product_fit);
    if (!overall && (band || cup)) overall = [band, cup].includes('small') ? 'small' : [band, cup].includes('large') ? 'large' : 'true';
    if (!overall) return '';
    if (band === 'small') return T('fit_band_tight', 'Some customer reviews flag that the band runs tight.');
    if (cup === 'small') return T('fit_cup_small', 'Some customer reviews flag that the cups run small.');
    if (band === 'large') return T('fit_band_loose', 'Some customer reviews flag that the band runs loose.');
    if (cup === 'large') return T('fit_cup_roomy', 'Some customer reviews flag that the cups run roomy.');
    if (overall === 'small') return T('fit_small', 'Some customer reviews flag that this style runs small.');
    if (overall === 'large') return T('fit_large', 'Some customer reviews flag that this style runs large.');
    return T('fit_true', 'Customers say this style fits true to size.');
  }

  // Neckline options carry a line drawing that overlays the button once picked
  // (Figma "25.06 OB Web Refresh", neckline component variants).
  // Question screens whose illustration flies in and lands instead of sitting still.
  // Each reuses a chip drawing, so no extra asset ships.
  // Intro backdrop: the six neckline drawings, mixed, raining down and settling.
  // Fixed layout (not random per load) so the composition is the same every time,
  // and only six images are ever fetched however many land.
  // Her intro composition, one entry per separate drawing (position as % of the box).
  const PIECES = [{"n":1,"x":12.41,"y":0.0,"w":60.28,"kb":13.5,"d":0.65},{"n":2,"x":71.57,"y":21.46,"w":28.43,"kb":4.9,"d":0.52},{"n":3,"x":0.0,"y":22.32,"w":31.3,"kb":12.5,"d":0.39},{"n":4,"x":70.56,"y":46.87,"w":28.61,"kb":7.4,"d":0.26},{"n":5,"x":27.96,"y":46.95,"w":30.83,"kb":5.6,"d":0.13},{"n":6,"x":18.15,"y":70.99,"w":81.85,"kb":15.2,"d":0.0}];

  const DROPS = [{"a":"balconette","x":54.9,"y":2.4,"w":31.1,"r":13.6,"d":0.03},{"a":"natural","x":-12.3,"y":20.9,"w":41.6,"r":6.8,"d":0.09},{"a":"balconette","x":-4.1,"y":65.8,"w":30.7,"r":13.1,"d":0.19},{"a":"pushup","x":28.6,"y":52.6,"w":37.6,"r":12.3,"d":0.2},{"a":"natural","x":-14.0,"y":53.7,"w":40.9,"r":-1.3,"d":0.27},{"a":"natural","x":59.2,"y":34.0,"w":41.9,"r":15.0,"d":0.34},{"a":"plunge","x":42.0,"y":84.4,"w":33.6,"r":16.9,"d":0.34},{"a":"scoop","x":1.0,"y":78.9,"w":35.0,"r":-12.6,"d":0.4},{"a":"plunge","x":33.0,"y":1.5,"w":36.1,"r":5.7,"d":0.51},{"a":"balconette","x":61.9,"y":84.8,"w":37.7,"r":-17.6,"d":0.56},{"a":"pushup","x":42.3,"y":13.1,"w":36.3,"r":-19.4,"d":0.57},{"a":"balconette","x":-8.4,"y":26.2,"w":35.6,"r":-2.6,"d":0.62},{"a":"plunge","x":52.0,"y":45.8,"w":34.6,"r":20.2,"d":0.72},{"a":"scoop","x":54.7,"y":54.7,"w":41.6,"r":11.4,"d":0.79},{"a":"scoop","x":-3.4,"y":4.5,"w":41.1,"r":-1.5,"d":0.79},{"a":"scoop","x":-1.4,"y":40.8,"w":33.5,"r":-18.9,"d":0.9},{"a":"bandeau","x":58.4,"y":68.6,"w":32.3,"r":10.2,"d":0.95},{"a":"bandeau","x":31.4,"y":31.1,"w":36.0,"r":7.1,"d":0.98}];

  const FLY_ART = { V2: 'plunge', V4: 'balconette' };

  const NECK_ART = {
    V3_scoop: 'scoop', V3_plunge: 'plunge', V3_bandeau: 'bandeau',
    V3_balconette: 'balconette', V3_natural: 'natural', V3_pushup: 'pushup',
  };

  class FitQuiz extends HTMLElement {
    connectedCallback() {
      this.runId = this.newRun();
      this.stage = this.querySelector('[data-fq-stage]');
      this.progress = this.querySelector('[data-fq-progress]');
      this.stepNo = this.querySelector('[data-fq-stepno]');
      this.closeBtn = this.querySelector('[data-fq-close]');   // absent on the page layout
      // The products JSON is a SIBLING of <fit-quiz> in the section, not a child —
      // search the document, and fail loudly instead of hanging on "Loading…".
      try {
        const src = this.querySelector('[data-fq-products]') || document.querySelector('[data-fq-products]');
        this.products = JSON.parse(src.textContent);
      } catch (e) {
        this.track('fit_quiz_error', { fq_stage: 'products', fq_message: this.errMsg(e) });
        this.querySelector('[data-fq-stage]').innerHTML =
          '<p class="fq__empty">The quiz could not read the product data on this page. (' + e.message + ')</p>';
        return;
      }
      this.sel = new Set();
      this.unit = this.dataset.defaultUnit === 'in' ? 'in' : 'cm';
      this.sizes = null;
      this.addedBra = null;
      // Close (only rendered when the quiz is used as an overlay).
      // Where they came from, captured at boot: the quiz never navigates, so the entry
      // referrer stays valid for the whole session. ?from=<handle> wins when the guide
      // was opened from a PDP's size guide, since that is the page they want back.
      const from = new URLSearchParams(location.search).get('from');
      this.exitUrl = from ? '/products/' + from
        : (document.referrer && document.referrer.indexOf(location.host) > -1 ? document.referrer : null);
      // Never history.back() as a fallback: the storefront redirect (myshopify ->
      // dk.organicbasics) puts an entry in the history of even a brand-new tab, so
      // "back" from a fresh visit landed on about:blank. exitUrl is already the
      // same-origin referrer when there is one; otherwise go home.
      this.exit = () => { window.location.href = this.exitUrl || '/'; };
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.exit);
      // On desktop the guide is a modal panel, so the scrim around it and Escape both
      // close it, the way any modal does. Neither applies at mobile widths, where the
      // guide simply is the page.
      document.addEventListener('click', (e) => {
        if (!this.isModal()) return;
        if (e.detail === 0) return;              // keyboard activation, not a real click
        // Hit-test by COORDINATES, not by element. Every click inside the quiz re-renders
        // the view, which detaches the clicked node before this listener runs — so
        // this.contains(target) is false for the quiz's own buttons and the guide closed
        // itself the moment anyone pressed "Get started". The panel's rectangle cannot be
        // detached.
        if (e.target && this.contains(e.target)) return;
        const r = this.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right &&
            e.clientY >= r.top && e.clientY <= r.bottom) return;
        // The site chrome has its own job and must not close the guide on the way to a link.
        const t = e.target;
        if (t && t.closest && t.closest('header, nav, footer, cart-drawer, dialog, [role="dialog"], [id*="shopify-section-header"], [class*="cookie"], [class*="klaviyo"]')) return;
        this.exit();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isModal()) this.exit();
      });
      this.flushPending();
      this.loadRestOfCatalogue();
      // Both files are small and independent; serialising them cost a round trip before
      // first paint. bra-size-rules.json is optional — if it fails we keep the copy baked
      // into fit-quiz-data.json.
      const rulesUrl = this.dataset.rulesUrl;
      Promise.all([
        fetch(this.dataset.dataUrl).then(r => r.json()),
        rulesUrl ? fetch(rulesUrl).then(r => r.json()).catch(() => null) : Promise.resolve(null),
      ]).then(([d, j]) => {
        this.D = d;
        // ONE source for sizing. assets/bra-size-rules.json is what the PDP's size finder
        // reads; the copy baked into fit-quiz-data.json is a build artefact and was
        // byte-identical, which is exactly the sort of pair that drifts apart unnoticed.
        // Whatever that file says wins; the baked copy is only the offline fallback.
        if (j && j.alpha && j.bandcup) {
          this.D.rules = { alpha: j.alpha, bandcup: j.bandcup, tol: j.gap_tolerance_cm || 1.5 };
          this.rulesVersion = j.version || null;
        }
        // 'underwear' (Figma "M Bra Style Guide 5") is pulled from the flow for now —
        // viewUnderwear() is kept so it is a one-word change to put it back.
        // QA mode (?qa=1): drops the email capture, so internal testers reach the
        // recommendations without creating a real Klaviyo profile, a real newsletter
        // subscriber, or triggering the live Welcome flow. Nothing is sent to Klaviyo
        // or /contact in this mode — see klaviyo() and postSignup(). URL-gated, so a
        // customer can never land in it by accident.
        this.qa = new URLSearchParams(location.search).get('qa') === '1';
        this.steps = this.qa
          ? ['intro', 'measure', 'V2', 'V3', 'V4', 'results']
          : ['intro', 'measure', 'V2', 'V3', 'V4', 'capture', 'results'];
        if (this.qa) this.dataset.qa = '1';
        if (this.qa) {
          const bar = document.createElement('p');
          bar.className = 'fq__qa';
          bar.textContent = this.T('qa_banner', 'QA mode \u2014 email capture is skipped and nothing is sent to Klaviyo or the newsletter.');
          this.prepend(bar);
        }
        this.i = 0;
        this.track('fit_quiz_open', { fq_from: new URLSearchParams(location.search).get('from') || '' });
        this.show();
      }).catch((e) => {
        // This catch wraps the WHOLE init chain, including show(), so a markup fault
        // lands here as well as a failed fetch — and it used to throw the exception away,
        // which is why a stray </div> read as "could not load quiz data" for eight days.
        this.track('fit_quiz_error', { fq_stage: 'init', fq_message: this.errMsg(e) });
        this.stage.innerHTML = '<p>' + this.T('err_data', 'Could not load quiz data.') + '</p>';
      });
    }

    // ---- wishlist (Rivo favourites) -------------------------------------------------
    // Rivo hydrates <rivo-favorite-button> ONCE at page load and never watches for nodes
    // added later — verified by injecting one into a collection page where 96 others work
    // and finding it stayed dead. Our cards are built at runtime, so that element could
    // never work here. Instead we draw our own heart and call Rivo's JS API directly,
    // which is the same action the PLP heart performs and is loaded on every page.
    rivo() {
      const r = window.RivoJS;
      return (r && typeof r.addFavoriteProduct === 'function') ? r : null;
    }

    // Which products are already favourited, fetched once per session.
    rivoFavourites() {
      if (this._favs) return this._favs;
      const r = this.rivo();
      if (!r || typeof r.getFavoriteProducts !== 'function') {
        this._favs = Promise.resolve([]);
        return this._favs;
      }
      this._favs = Promise.resolve()
        .then(() => r.getFavoriteProducts())
        .then(list => (Array.isArray(list) ? list : (list && list.data) || [])
          .map(x => String((x && (x.product_id || x.id)) || x)))
        .catch(() => []);
      return this._favs;
    }

    // Wire one heart. Falls back to a plain product link when Rivo is unavailable, so the
    // control is never a dead end.
    bindWish(btn, prod) {
      if (!btn) return;
      const id = prod && prod.id;
      const r = this.rivo();
      if (!id || !r) {
        btn.setAttribute('href', this.pdpUrl(prod, null));
        return;
      }
      btn.removeAttribute('href');
      btn.setAttribute('role', 'button');
      btn.setAttribute('tabindex', '0');
      btn.setAttribute('aria-pressed', 'false');
      const paint = (on) => {
        btn.setAttribute('aria-pressed', String(!!on));
        btn.classList.toggle('fq__wish--on', !!on);
        btn.setAttribute('aria-label', (on ? 'Remove ' : 'Add ') + (prod.title || 'this product') + (on ? ' from' : ' to') + ' favourites');
      };
      this.rivoFavourites().then(ids => paint(ids.indexOf(String(id)) > -1));
      const toggle = () => {
        const on = btn.getAttribute('aria-pressed') === 'true';
        paint(!on);                                    // optimistic: the tap must feel instant
        Promise.resolve()
          .then(() => (on ? r.removeFavoriteProduct(id) : r.addFavoriteProduct(id)))
          .then(() => { this._favs = null; })          // let the next card re-read the truth
          .catch(() => {
            paint(on);                                 // put it back if Rivo refused
            if (window.console && console.warn) console.warn('[fit-quiz] Rivo favourite failed');
          });
      };
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();   // the page-mode scrim handler would otherwise close the guide
        toggle();
      });
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    }

    // Shopify's paginate renders one page and caps it at 250 products, and the bra
    // collection is bigger than that — so the tail of it (a whole style, plus colourways
    // of styles that were only partly present) never reached the ranking. The section
    // reports how many pages there are; the rest are pulled here and merged. A failure
    // leaves the quiz working on page one rather than breaking it.
    loadRestOfCatalogue() {
      const total = Number(this.products && this.products.pages) || 1;
      this.pagesDone = total <= 1;
      if (this.pagesDone) { this.pagesReady = Promise.resolve(); return; }
      const seen = new Set((this.products.bras || []).map(p => p.id || p.handle));
      const fetchPage = (n) => {
        // Ask for the SECTION, not the page. Re-fetching the whole document pulled the
        // theme's header, footer, menus and scripts a second time — 1.39MB on DK, of
        // which only the products JSON was wanted.
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(n));
        url.searchParams.set('sections', 'fit-quiz');
        return fetch(url.toString(), { credentials: 'same-origin' })
          .then(r => r.json())
          .then(j => {
            // {"fit-quiz": null} is what Shopify serves when the section render timed out,
            // and it is CACHED, so it comes back fast and looks like a normal response.
            // Returning false here rather than swallowing it is the whole point: a lost
            // page is a lost slice of the catalogue, and ranking cannot tell the
            // difference between "this style is unavailable" and "this style is on the
            // page that never arrived".
            const html = j['fit-quiz'];
            if (!html) return false;
            const el = new DOMParser().parseFromString(html, 'text/html')
              .querySelector('script[data-fq-products]');
            if (!el) return false;
            (JSON.parse(el.textContent).bras || []).forEach(p => {
              const k = p.id || p.handle;
              if (seen.has(k)) return;
              seen.add(k);
              this.products.bras.push(p);
            });
            return true;
          })
          .catch(() => false);
      };
      // Retry a lost page before giving up on it. The failure is a render timeout on the
      // store's side and it clusters, so a short backoff is worth more than an immediate
      // second try.
      const fetchWithRetry = (n, attempt) => fetchPage(n).then(ok => {
        if (ok || attempt >= 3) return ok;
        return new Promise(r => setTimeout(r, 400 * attempt)).then(() => fetchWithRetry(n, attempt + 1));
      });
      const pages = [];
      for (let n = 2; n <= total; n++) pages.push(n);
      // Fetch with bounded concurrency rather than one after another. These pages are
      // independent, and the results screen blocks on all of them landing — strictly
      // sequential meant total merge time scaled with the page count, which is the cost
      // that made a smaller page size unattractive. Four at a time: enough to keep the
      // merge well inside the time she spends answering, without asking the store for a
      // dozen section renders at once, which is the thing we are trying to make cheaper.
      const LANES = 4;
      let next = 0;
      const lost = [];
      const lane = () => {
        if (next >= pages.length) return Promise.resolve();
        const n = pages[next++];
        return fetchWithRetry(n, 1).then(ok => { if (!ok) lost.push(n); }).then(lane);
      };
      this.pagesReady = Promise.all(
        Array.from({ length: Math.min(LANES, pages.length) }, lane)
      ).then(() => {
          this._groups = null;   // the grouping cache was built from page one only
          this.pagesDone = true;
          // pagesDone means "we stopped waiting", NOT "we have everything" — it used to
          // mean both, because every failure path resolved silently and a session that
          // lost a page was indistinguishable from one that got them all. Ranking then
          // scored against a partial range and produced a confident, badged, wrong answer.
          // Found by ob-theme-perf-87, 2026-09-17.
          this.pagesLost = lost;
          this.pagesComplete = lost.length === 0;
          if (lost.length && window.console && console.warn) {
            console.warn('[fit-quiz] catalogue incomplete — page(s) ' + lost.join(', ') + ' never arrived');
          }
        });
    }

    go(dir) {
      this.i = Math.max(0, Math.min(this.steps.length - 1, this.i + dir));
      this.show(dir < 0);
    }

    // Footer wording follows the comps: "Question 0 of 5" on the intro, counting the
    // answerable steps only (measure + the question screens), results excluded.
    questionCount() {
      const skip = ['intro', 'capture', 'results', 'upsell'];
      return this.steps.filter(s => skip.indexOf(s) === -1).length;
    }

    show(back) {
      const step = this.steps[this.i];
      // show() also re-renders the SAME step (results waiting on later pages, image
      // hydration), so only a change of step is an event.
      if (step !== this._trackedStep) {
        this._trackedStep = step;
        if (step !== 'intro') {
          if (!this._startSent) { this._startSent = true; this.track('fit_quiz_start', {}); }
          this.track('fit_quiz_step', { fq_step: step, fq_step_index: this.i });
        }
      }
      const total = this.questionCount();
      const n = step === 'intro' ? 0 : Math.min(this.i, total);
      // Same pill as the cart's free-shipping threshold: green fill grows to 100%.
      this.progress.style.width = Math.round(n / total * 100) + '%';
      this.stepNo.textContent = step === 'results' ? 'Your matches'
        : step === 'upsell' ? 'One more thing'
        : step === 'capture' ? 'Almost there'
        : `Question ${n} of ${total}`;
      const view = document.createElement('div');
      view.className = 'fq__view' + (back ? ' is-back' : '');
      if (step === 'intro') this.viewIntro(view);
      else if (step === 'underwear') this.viewUnderwear(view);
      else if (step === 'capture') this.viewCapture(view);
      else if (step === 'measure') this.viewMeasure(view);
      else if (step === 'fabric') this.viewFabric(view);
      else if (step === 'results') this.viewResults(view);
      else if (step === 'upsell') this.viewUpsell(view);
      else this.viewQuestion(view, step);
      // the intro's consent-overlay poll belongs to the intro only
      if (this._pileTimer) { clearTimeout(this._pileTimer); this._pileTimer = null; }
      this.dataset.step = step;
      // The way out only appears on the last screen; before that the flow has its own
      // back arrow and an X would read as "abandon this".
      // On mobile the X is the way out of the last screen only; on desktop the guide
      // is a modal panel, and a modal must be closable at every step.
      if (this.closeBtn) {
        const modal = this.isModal();
        this.closeBtn.hidden = !modal && step !== 'upsell';
      }
      this.stage.innerHTML = '';
      this.stage.appendChild(view);
      const f = view.querySelector('input, button.fq__chip');
      if (f && step === 'measure') f.focus({ preventScroll: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }


    // ---- step 0: intro -------------------------------------------------------------
    viewIntro(view) {
      // The intro is the designer's composition split into its six drawings, each
      // falling in and landing. The MP4 / poster / static fallbacks it replaced were
      // unreachable from the moment the pile shipped (pileBase is always set), so they
      // and their assets were removed on 2026-09-14 — see git history to restore.
      const pileBase = this.dataset.introPileBase;
      const backdrop = pileBase
        ? `<div class="fq__pile" aria-hidden="true">${PIECES.map(pc =>
            `<img class="fq__piece" src="${pileBase.replace('__N__', pc.n)}" alt=""
               style="left:${pc.x}%;top:${pc.y}%;width:${pc.w}%;animation-delay:${pc.d}s">`
          ).join('')}</div>`
        : '';
      view.innerHTML = `
        <div class="fq__intro">
          ${backdrop}
          <div class="fq__hero">
            <h2 class="fq__title fq__title--intro">${this.T('intro_title_1', 'The Bra')}<span>${this.T('intro_title_2', 'Style')}</span><span>${this.T('intro_title_3', 'Guide')}</span></h2>
          </div>
          <div class="fq__introbox">
            <p><b>${this.T('intro_body_strong', 'Let us help you scope out the best bra based on your needs.')}</b>
               Take this 2 minute quiz and we'll give you our best recommendation.</p>
          </div>
          <div class="fq__nav"><button class="fq__next" data-next>${this.T('intro_cta', 'Get started')}</button></div>
        </div>`;
      view.querySelector('[data-next]').addEventListener('click', () => this.go(1));
      this.startPile(view.querySelector('.fq__pile'));
    }

    // Release the falling artwork only once the page has stopped loading AND the six
    // drawings have decoded. Left to run on render it played from ~0.7s to ~2.5s after
    // navigation — the whole fall happened while the theme was still painting, so the
    // first thing a customer actually saw was the landed pile. The 3s cap means a slow
    // connection still gets the fall rather than no animation at all.
    startPile(pile) {
      if (!pile) return;
      const go = () => pile.classList.add('is-go');
      const imgs = [...pile.querySelectorAll('img')];
      const decoded = Promise.all(imgs.map(i =>
        i.decode ? i.decode().catch(() => {}) : Promise.resolve()));
      const settled = document.readyState === 'complete'
        ? Promise.resolve()
        : new Promise(r => window.addEventListener('load', r, { once: true }));
      // ...and until nothing is sitting on top of the page. On a first visit the
      // consent dialog covers the whole intro, so the fall would play out behind it
      // and the first thing seen after accepting would be the landed pile. Asking the
      // document what is actually at the artwork's centre covers any consent vendor
      // without naming one.
      const clear = new Promise(resolve => {
        const t0 = Date.now();
        const tick = () => {
          // getBoundingClientRect + elementFromPoint both force synchronous layout, so
          // this must stop the moment the intro leaves the screen. Without the cancel in
          // show() it kept hit-testing behind the question screens for the full 20s.
          if (!pile.isConnected) { this._pileTimer = null; return resolve(); }
          const r = pile.getBoundingClientRect();
          const x = Math.round(r.left + r.width / 2);
          const y = Math.round(Math.min(Math.max(r.top + r.height / 2, 4), window.innerHeight - 4));
          const el = document.elementFromPoint(x, y);
          if (!el || this.contains(el) || Date.now() - t0 > 20000) { this._pileTimer = null; return resolve(); }
          this._pileTimer = setTimeout(tick, 250);
        };
        tick();
      });
      const capped = new Promise(r => setTimeout(r, 25000));
      Promise.race([Promise.all([decoded, settled, clear]), capped])
        .then(() => requestAnimationFrame(() => requestAnimationFrame(go)));
    }

    // Custom listbox for the unit picker — a native <select> cannot take the comp's
    // open state (bordered box, options listed, the hovered one on soft green). Value
    // lives on a hidden input so everything downstream reads it the same way.
    bindSelect(root) {
      if (!root) return;
      const btn = root.querySelector('[data-unitbtn]');
      const list = root.querySelector('[data-unitlist]');
      const label = root.querySelector('[data-unitlabel]');
      const input = root.querySelector('input[type="hidden"]');
      const opts = [...list.querySelectorAll('[role="option"]')];
      const current = opts.find(o => o.dataset.val === input.value) || opts[0];
      label.textContent = current.textContent.trim();
      opts.forEach(x => x.setAttribute('aria-selected', String(x === current)));
      const open = (on) => {
        list.hidden = !on;
        btn.setAttribute('aria-expanded', String(on));
        root.classList.toggle('is-open', on);
        if (on) (opts.find(o => o.dataset.val === input.value) || opts[0]).focus();
      };
      const choose = (o) => {
        input.value = o.dataset.val;
        label.textContent = o.textContent.trim();
        opts.forEach(x => x.setAttribute('aria-selected', String(x === o)));
        open(false);
        btn.focus();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      btn.addEventListener('click', () => open(list.hidden));
      list.addEventListener('click', (e) => {
        const o = e.target.closest('[role="option"]');
        if (o) choose(o);
      });
      list.addEventListener('keydown', (e) => {
        const i = opts.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          opts[(i + (e.key === 'ArrowDown' ? 1 : opts.length - 1)) % opts.length].focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault(); if (i > -1) choose(opts[i]);
        } else if (e.key === 'Escape') { open(false); btn.focus(); }
      });
      document.addEventListener('click', (e) => {
        if (!list.hidden && !root.contains(e.target)) open(false);
      });
      opts.forEach(o => { o.tabIndex = -1; });
    }

    // Back row under the CTA (comps 2026-09-09). The right-hand slot carries "Skip ›" on
    // the question steps: restored 2026-09-16, because not every customer has an answer to
    // every question and gating Next on one was losing them. The measurement step's own
    // "Skip size" CTA is a separate, deliberate affordance.
    subnav(right) {
      return `<div class="fq__subnav">
          <button type="button" data-back>${this.T('nav_back', '&lsaquo; Back')}</button>
          ${right || '<span></span>'}
        </div>`;
    }

    bindSubnav(view) {
      const b = view.querySelector('[data-back]');
      if (b) b.addEventListener('click', () => this.go(-1));
    }

    // ---- step 1: measurements ----------------------------------------------------
    viewMeasure(view) {
      const unitSuffix = this.unit === 'in' ? 'inches' : 'CM';
      view.innerHTML = `
        <h2 class="fq__title">${this.T('measure_title_1', 'What are your')}<span>${this.T('measure_title_2', 'measurements?')}</span></h2>
        <p class="fq__hint">${this.T('measure_intro', 'Take your measurements and enter them into the fields below.')}</p>
        <div class="fq__fields">
          <div class="fq__field--unit">
            <label class="fq__label" for="fq-unit-btn">Choose measurements</label>
            <div class="fq__select" data-unitselect>
              <input type="hidden" id="fq-unit" value="cm">
              <button type="button" class="fq__select-btn" id="fq-unit-btn" data-unitbtn
                      aria-haspopup="listbox" aria-expanded="false">
                <span data-unitlabel>Centimeters</span>
                <svg viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
              </button>
              <ul class="fq__select-list" role="listbox" aria-labelledby="fq-unit-btn" hidden data-unitlist>
                <li role="option" data-val="cm" aria-selected="true">Centimeters</li>
                <li role="option" data-val="in" aria-selected="false">Inches</li>
              </ul>
            </div>
          </div>
          <div class="fq__pair">
            <div>
              <label class="fq__label" for="fq-ub"><span class="fq__num">1</span> Underband</label>
              <input id="fq-ub" type="number" inputmode="decimal" min="40" max="160" placeholder="${this.T('measure_underband_ph', 'Enter your underband size in %s').replace('%s', this.unit === 'in' ? 'inches' : 'CM')}">
            </div>
            <div>
              <label class="fq__label" for="fq-bust"><span class="fq__num">2</span> Bust</label>
              <input id="fq-bust" type="number" inputmode="decimal" min="40" max="180" placeholder="${this.T('measure_bust_ph', 'Enter your bust size in %s').replace('%s', this.unit === 'in' ? 'inches' : 'CM')}">
            </div>
          </div>
        </div>
        <div class="fq__sizeline" data-line hidden></div>
        <p class="fq__note" data-note hidden></p>
        <div class="fq__nav"><button class="fq__next" data-next disabled>${this.T('nav_next', 'Next')}</button></div>
        ${this.subnav(`<button type="button" data-skipsize>${this.T('measure_skip', 'Skip Size')} &rsaquo;</button>`)}
        <button type="button" class="fq__helppill" data-help aria-expanded="false" aria-controls="fq-howto">
          <span>${this.T('measure_help', 'How do I measure correct?')}</span>
          <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="none" stroke="currentColor"/><path d="M8 6l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        ${this.dataset.measureImg ? `
          <figure class="fq__howto" id="fq-howto" data-howto hidden>
            <img class="fq__measure-img" src="${this.dataset.measureImg}" alt="Where to measure: (1) underband, (2) bust" loading="lazy" width="355" height="442">
            <figcaption class="fq__howto-steps">
              <h3><span class="fq__num">1</span> ${this.T('measure_h_underband', 'Underband size')}</h3>
              <p>Wrap a measuring tape snugly around your ribcage, just under your bust. Make sure the tape is
                 level all the way around. Add the measurement in the calculator above.</p>
              <h3><span class="fq__num">2</span> ${this.T('measure_h_bust', 'Bust size')}</h3>
              <p>Measure around the fullest part of your bust, keeping the tape level and not too tight.
                 Add the measurement in the calculator above.</p>
            </figcaption>
          </figure>` : ''}`;
      this.bindSubnav(view);
      // Measuring is the only step that asks for effort rather than a tap, so it is the
      // one people abandon. Skipping keeps them in the flow; it just means we must not
      // pretend to know their size afterwards.
      view.querySelector('[data-skipsize]').addEventListener('click', () => {
        this.sizes = null;
        this.sizeSkipped = true;
        this.go(1);
      });
      // The guide is collapsed until asked for; the pill is the disclosure control.
      const help = view.querySelector('[data-help]');
      const howto = view.querySelector('[data-howto]');
      if (help && howto) help.addEventListener('click', () => {
        const open = help.getAttribute('aria-expanded') === 'true';
        help.setAttribute('aria-expanded', String(!open));
        howto.hidden = open;
        if (!open) howto.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      const unit = view.querySelector('#fq-unit'), ub = view.querySelector('#fq-ub'), bust = view.querySelector('#fq-bust');
      const next = view.querySelector('[data-next]'), line = view.querySelector('[data-line]'), note = view.querySelector('[data-note]');
      unit.value = this.unit;
      this.bindSelect(view.querySelector('[data-unitselect]'));
      if (this.ubVal) ub.value = this.ubVal;
      if (this.bustVal) bust.value = this.bustVal;
      const upd = () => {
        this.unit = unit.value;
        const suffix = this.unit === 'in' ? 'inches' : 'CM';
        // Match the size guide's calculator, which says "Enter your underband size in CM"
        // (Alexander, 18 Sep). Three faults compounded here: `suffix` was computed on the
        // line above and then ignored, the copy was hardcoded rather than read from the
        // i18n blob, and so fit_quiz.measure_underband_ph / measure_bust_ph were DEAD —
        // published by the section, translated by nobody, read by nothing. They drive it
        // now, with %s taking the unit exactly as the calculator's own copy does.
        ub.placeholder = this.T('measure_underband_ph', 'Enter your underband size in %s').replace('%s', suffix);
        bust.placeholder = this.T('measure_bust_ph', 'Enter your bust size in %s').replace('%s', suffix);
        let u = parseFloat(String(ub.value).replace(',', '.')), b = parseFloat(String(bust.value).replace(',', '.'));
        this.ubVal = ub.value; this.bustVal = bust.value;
        if (!(u > 0) || !(b > 0) || b <= u) { this.sizes = null; next.disabled = true; line.hidden = note.hidden = true; return; }
        if (this.unit === 'in') { u *= IN_TO_CM; b *= IN_TO_CM; }
        // The resolved size is kept for the results screen, not shown here (design 2026-09-09).
        const s = resolveSizes(this.D.rules, u, b, this.dataset.sizeRegion || 'eu');
        this.sizes = s;
        this.sizeSkipped = false;   // they came back and measured after all
        line.hidden = true;
        // Three states, and the middle one is new. She is inside the range, outside it, or
        // her measurements land on a cell the chart has no bra for — in which case the fit
        // team's rule gives her the nearest cup on her OWN underband, and we say so rather
        // than presenting it as her size (Ida, 2026-09-18).
        note.textContent = (s.alpha || s.bandcup)
          ? (s.approx ? this.T('size_approx', 'We don\'t make your exact size \u2014 this is the closest we do, on your underband.') : '')
          : this.T('size_out', 'These measurements fall outside our current size range.');
        note.hidden = !note.textContent;
        next.disabled = !(s.alpha || s.bandcup);
      };
      [unit, ub, bust].forEach(el => el.addEventListener('input', upd));
      [ub, bust].forEach(el => el.addEventListener('keydown', e => { if (e.key === 'Enter' && !next.disabled) this.go(1); }));
      next.addEventListener('click', () => this.go(1));
      upd();
    }

    // ---- steps 2-4: draft questions ---------------------------------------------
    viewQuestion(view, qid) {
      const q = this.D.questions.find(x => x.id === qid);
      const parts = q.text.replace(/ \(pick up to 2\)/i, '').split(/(?<=\?)/);
      // The art hangs off the heading, overlapping it like the comps — and only on the
      // questions that have it there (not the neckline screen).
      const withArt = qid === 'V2' || qid === 'V4';
      // A one-off flourish on the first question screen: the plunge drawing crosses the
      // viewport top-left to bottom-right and exits. Same asset the chips use, so it
      // costs nothing extra. Reduced-motion drops it entirely (CSS).
      // On the first question screen the drawing flies in and comes to rest exactly
      // where the static illustration used to sit — so it replaces it rather than
      // passing over it. Elsewhere the static one still applies.
      const flyName = FLY_ART[qid];
      const flies = !!flyName && !!this.dataset.artBase;
      const art = flies
        ? `<span class="fq__art fq__art--q fq__fly fq__fly--${qid.toLowerCase()}" aria-hidden="true"><img src="${this.dataset.artBase.replace('__NAME__', flyName)}" alt=""></span>`
        : '';   // the static illustration is gone: V2 and V4 both fly (2026-09-14)
      view.innerHTML = `
        <div class="fq__hero${withArt || flies ? ' fq__hero--q' : ''}">
          <h2 class="fq__title">${parts[0] || q.text}</h2>
          ${art}
        </div>
        <p class="fq__selecthint">Select up to 2</p>
        <div class="fq__chips${qid === 'V3' ? ' fq__chips--art' : ''}">${q.answers.map(a => {
          const art = NECK_ART[a.id] && this.dataset.artBase
            ? `<img class="fq__chip-art fq__chip-art--${NECK_ART[a.id]}" src="${this.dataset.artBase.replace('__NAME__', NECK_ART[a.id])}" alt="" loading="lazy">` : '';
          return `<button type="button" class="fq__chip${art ? ' fq__chip--art' : ''}" data-a="${a.id}" aria-pressed="${this.sel.has(a.id)}"><span>${a.label}</span>${art}</button>`;
        }).join('')}</div>
        <div class="fq__nav"><button class="fq__next" data-next disabled>${this.T('nav_next', 'Next')}</button></div>
        ${this.subnav(`<button type="button" data-skip>${this.T('nav_skip', 'Skip')} &rsaquo;</button>`)}`;
      this.bindSubnav(view);
      const next = view.querySelector('[data-next]');
      // Next means "these are my answers", so it stays disabled until she has actually
      // picked one; Skip is the separate, explicit "no answer" path.
      // Re-checked after every chip so deselecting the last one disables it again.
      const syncNext = () => { next.disabled = !q.answers.some(a => this.sel.has(a.id)); };
      syncNext();
      view.querySelectorAll('.fq__chip').forEach(c => c.addEventListener('click', () => {
        const on = c.getAttribute('aria-pressed') === 'true';
        if (on) this.sel.delete(c.dataset.a);
        else {
          const picked = q.answers.filter(a => this.sel.has(a.id));
          if (picked.length >= 2) this.sel.delete(picked[0].id);
          this.sel.add(c.dataset.a);
        }
        view.querySelectorAll('.fq__chip').forEach(x => x.setAttribute('aria-pressed', this.sel.has(x.dataset.a)));
        syncNext();
      }));
      next.addEventListener('click', () => { if (!next.disabled) this.go(1); });
      // A skipped question carries no answer: drop anything picked here before moving on,
      // so a half-answered-then-skipped question cannot leak into the ranking or the
      // Klaviyo profile.
      const skip = view.querySelector('[data-skip]');
      if (skip) skip.addEventListener('click', () => {
        q.answers.forEach(a => this.sel.delete(a.id));
        this.go(1);
      });
    }

    // ---- optional fabric preference (matches the product's material family) --------
    viewFabric(view) {
      const mats = [...new Set(this.products.bras.map(p => p.mat).filter(m => m && m !== 'null'))];
      this.fabrics = this.fabrics || new Set();
      view.innerHTML = `
        <h2 class="fq__title">Do you have a<span>fabric preference?</span></h2>
        <p class="fq__selecthint">Optional — select up to 2</p>
        <div class="fq__chips">${mats.map(m =>
          `<button type="button" class="fq__chip" data-m="${m}" aria-pressed="${this.fabrics.has(m)}">${m.charAt(0) + m.slice(1).toLowerCase()}</button>`).join('')}</div>
        <div class="fq__nav"><button class="fq__next" data-next>Next</button></div>
        ${this.subnav()}`;
      this.bindSubnav(view);
      view.querySelectorAll('.fq__chip').forEach(c => c.addEventListener('click', () => {
        const m = c.dataset.m;
        if (this.fabrics.has(m)) this.fabrics.delete(m);
        else {
          if (this.fabrics.size >= 2) this.fabrics.delete([...this.fabrics][0]);
          this.fabrics.add(m);
        }
        view.querySelectorAll('.fq__chip').forEach(x => x.setAttribute('aria-pressed', this.fabrics.has(x.dataset.m)));
      }));
      view.querySelector('[data-next]').addEventListener('click', () => this.go(1));
    }

    // A chosen fabric is a constraint, not a preference (D-003): products in other
    // materials are removed. Honest empty state if nothing passes.
    passesFabric(sn) {
      if (!this.fabrics || !this.fabrics.size) return true;
      return this.productFor(sn).some(p => this.fabrics.has(p.mat));
    }

    // ---- underwear cross-sell (prototype question 5) --------------------------------
    // Styles + size are zero-party data for the upsell; they carry no bra weighting
    // (spec Q8), so nothing here changes the ranking.
    viewUnderwear(view) {
      const STYLES = ['Briefs', 'Thongs', 'Shorties', 'Hipsters', 'High-Rise', 'Boxer Briefs'];
      const SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
      this.uwStyles = this.uwStyles || new Set();
      view.innerHTML = `
        <h2 class="fq__title">We make<span>underwear too!</span></h2>
        <p class="fq__selecthint">Select your fave styles</p>
        <div class="fq__chips">${STYLES.map(t =>
          `<button type="button" class="fq__chip" data-uw="${t}" aria-pressed="${this.uwStyles.has(t)}">${t}</button>`).join('')}</div>
        <p class="fq__selecthint">What's your underwear size?</p>
        <div class="fq__sizes">${SIZES.map(t =>
          `<button type="button" class="fq__size-chip" data-uwsize="${t}" aria-pressed="${this.uwSize === t}">${t}</button>`).join('')}</div>
        <div class="fq__nav"><button class="fq__next" data-next>Next</button></div>
        ${this.subnav()}`;
      this.bindSubnav(view);
      view.querySelectorAll('[data-uw]').forEach(c => c.addEventListener('click', () => {
        const t = c.dataset.uw;
        if (this.uwStyles.has(t)) this.uwStyles.delete(t); else this.uwStyles.add(t);
        view.querySelectorAll('[data-uw]').forEach(x => x.setAttribute('aria-pressed', this.uwStyles.has(x.dataset.uw)));
      }));
      view.querySelectorAll('[data-uwsize]').forEach(c => c.addEventListener('click', () => {
        this.uwSize = this.uwSize === c.dataset.uwsize ? null : c.dataset.uwsize;
        view.querySelectorAll('[data-uwsize]').forEach(x => x.setAttribute('aria-pressed', this.uwSize === x.dataset.uwsize));
      }));
      view.querySelector('[data-next]').addEventListener('click', () => this.go(1));
    }

    // ---- capture (before results) ---------------------------------------------------
    // The email is required (Alexander, 2026-09-09): the results only unlock once a valid
    // address is entered.
    viewCapture(view) {
      const offer = this.dataset.captureOffer;
      view.innerHTML = `
        <div class="fq__hero fq__hero--capture">
          <h2 class="fq__title">${this.T('capture_title_1', 'Your style')}<span>${this.T('capture_title_2', 'recommendations')}</span><span>${this.T('capture_title_3', 'are ready!')}</span></h2>
        </div>
        <p class="fq__hint fq__hint--center">We've picked the bras and underwear we think you'll love.
          Enter your email and we'll send them straight to your inbox.</p>
        ${offer ? `<p class="fq__offer">${offer}</p>` : ''}
        <div class="fq__fields">
          <label class="fq__label" for="fq-email">${this.T('capture_email_label', 'E-mail')}</label>
          <input id="fq-email" type="email" inputmode="email" autocomplete="email" placeholder="Email">
          <p class="fq__error" data-err hidden></p>
        </div>
        <label class="fq__consent">
          <input type="checkbox" id="fq-consent" data-consent>
          <span>Yes — email me my results and sign me up to the newsletter. I agree to the
            <a href="${this.dataset.termsUrl || '/policies/terms-of-service'}" target="_blank" rel="noopener">${this.T('capture_terms', 'Terms of Use')}</a>
            and <a href="${this.dataset.privacyUrl || '/policies/privacy-policy'}" target="_blank" rel="noopener">${this.T('capture_privacy', 'Privacy Policy')}</a>.
            You may unsubscribe at any time.</span>
        </label>
        <div class="fq__nav"><button class="fq__next" data-next>${this.T('capture_cta', 'Get my results')}</button></div>
        <div class="fq__subnav">
          <button type="button" data-back>${this.T('nav_back', '&lsaquo; Back')}</button>
          <span></span>
        </div>`;
      this.bindSubnav(view);
      const email = view.querySelector('#fq-email');
      const err = view.querySelector('[data-err]');
      const consent = view.querySelector('[data-consent]');
      // Nothing leaves the page without BOTH an address and a ticked box (Alexander's
      // call: no opt-out path from this screen).
      const go = () => {
        const v = email.value.trim();
        if (!v) {
          err.textContent = 'Enter your email to see your results.';
          err.hidden = false; email.focus(); return;
        }
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
          err.textContent = 'That email doesn\'t look right — check it and try again.';
          err.hidden = false; email.focus(); return;
        }
        if (!consent.checked) {
          err.textContent = 'Tick the box so we can email your results and sign you up.';
          err.hidden = false; consent.focus(); return;
        }
        this.email = v;
        this.consentAt = new Date().toISOString();
        this.subscribe(v);                             // fire and forget; results never wait on it
        this.klaviyoIdentify(v);
        this.go(1);
      };
      email.addEventListener('input', () => { err.hidden = true; });
      consent.addEventListener('change', () => { err.hidden = true; });
      view.querySelector('[data-next]').addEventListener('click', go);
      email.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); go(); } });
    }

    // Shopify's own customer form endpoint — same list a footer signup writes to, tagged so
    // quiz signups stay identifiable. Klaviyo mirroring happens downstream, per the spec.
    subscribe(email) {
      this.postSignup({ email: email, at: this.consentAt || new Date().toISOString() }, 0);
    }

    // A consented signup must not be lost to a flaky network or a closed tab:
    // keepalive keeps the request alive past unload, one retry covers a blip, and
    // anything still failing is parked in localStorage and replayed on the next load.
    postSignup(p, attempt) {
      if (this.qa) return Promise.resolve();   // no newsletter signup from a QA run
      const body = new URLSearchParams({
        form_type: 'customer', utf8: '\u2713',
        'contact[email]': p.email,
        'contact[tags]': 'newsletter, fitquiz',
        'contact[accepts_marketing]': 'true',
      });
      fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body,
        keepalive: true,
      }).then(r => {
        // A bot-protection challenge page answers HTTP 200 after a redirect, so r.ok alone reads
        // it as success while no customer is created. No customer on any store has ever carried
        // the fitquiz tag; this records which of ok / challenge / http_error is happening.
        const challenged = !!(r.url && /\/challenge/.test(r.url));
        this.track('fit_quiz_signup', { fq_outcome: challenged ? 'challenge' : (r.ok ? 'ok' : 'http_' + r.status) });
        if (!r.ok) throw new Error('contact ' + r.status);
        this.pending(null);
      }).catch(() => {
        if (attempt < 1) { setTimeout(() => this.postSignup(p, attempt + 1), 1500); return; }
        this.track('fit_quiz_error', { fq_stage: 'signup', fq_message: 'shopify contact post failed twice' });
        this.pending(p);
      });
    }

    // read with no argument, write with a value, clear with null
    pending(v) {
      const K = 'ob-fitquiz-pending-signup';
      try {
        if (v === undefined) { const r = localStorage.getItem(K); return r ? JSON.parse(r) : null; }
        if (v === null) localStorage.removeItem(K); else localStorage.setItem(K, JSON.stringify(v));
      } catch (e) { /* private mode: best effort only */ }
      return null;
    }

    flushPending() {
      const p = this.pending();
      // a consent older than 30 days is stale — drop it rather than act on it
      if (!p || !p.email) return;
      if (p.at && Date.now() - Date.parse(p.at) > 30 * 864e5) { this.pending(null); return; }
      this.postSignup(p, 0);
    }

    // ---- Klaviyo ------------------------------------------------------------------
    // OB runs four Klaviyo accounts, one per store. We never name one: the Shopify
    // integration already loads that store's onsite script, so window.klaviyo is
    // ALWAYS the right account. No keys, no per-store branching, nothing to configure.
    // Consent stays with the Shopify /contact post above; these calls carry quiz data.
    klaviyo() {
      // QA mode never reaches the real account — the four stores each load their own
      // onsite script, so an internal test would otherwise write a live profile.
      if (this.qa) return null;
      var k = window.klaviyo;
      return (k && typeof k.identify === 'function' && typeof k.track === 'function') ? k : null;
    }

    // Which answers she picked, by their human labels rather than internal ids.
    answerLabels() {
      const out = {};
      (this.D.questions || []).forEach(q => {
        const picked = q.answers.filter(a => this.sel.has(a.id)).map(a => a.label);
        if (picked.length) out[q.id] = picked;
      });
      return out;
    }

    // Profile properties — these persist on the profile, so they are what segments and
    // later flows can use ("everyone whose quiz size is a D cup").
    quizProfile() {
      const sz = this.sizes || {};
      const a = this.answerLabels();
      return {
        FitQuizCompletedAt: new Date().toISOString(),
        FitQuizSizeBandCup: sz.bandcup || '',
        FitQuizSizeAlpha: sz.alpha || '',
        FitQuizSize: [sz.bandcup, sz.alpha].filter(Boolean).join(' / '),
        FitQuizPriorities: a.V2 || [],
        FitQuizNecklines: a.V3 || [],
        FitQuizFeatures: a.V4 || [],
        FitQuizVersion: this.dataset.quizVersion || 'v3-draft',
        FitQuizConsentAt: this.consentAt || '',
      };
    }

    // Subscribe to the Klaviyo list directly, at the moment she consents.
    //
    // Why this exists: the quiz already posts to Shopify's /contact form, which sets
    // marketing consent on a Shopify customer and reaches Klaviyo through the Shopify
    // integration's sync. But the results flow fires on an event that lands immediately,
    // and its "is in Newsletter" filter is evaluated minutes later — so if the sync has
    // not caught up, a customer who did everything right is silently dropped and never
    // emailed. Subscribing here makes list membership immediate and independent of it.
    //
    // Public company id, never a private key: it is read off the store's own onsite
    // script, so each of the four stores subscribes into its own account automatically.
    klaviyoCompanyId() {
      const el = document.querySelector('script[src*="klaviyo.com/onsite/js/"]');
      const m = el && el.src.match(/onsite\/js\/([A-Za-z0-9]+)\//);
      return m ? m[1] : null;
    }

    klaviyoSubscribe(email) {
      if (this.qa) return Promise.resolve();          // never from a QA run
      const listId = this.dataset.klaviyoList;
      const company = this.klaviyoCompanyId();
      if (!listId || !company || !email) return Promise.resolve();
      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'The Bra Style Guide',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      };
      // revision MUST be 2026-07-15 or later: earlier revisions reject the consent shape
      // with "'subscriptions' is not a valid field for the resource 'profile'". This
      // shipped once on 2024-10-15 and failed silently on every run — nobody reached the
      // Newsletter list — because the response was never checked. So check it.
      return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify(body),
        keepalive: true
      }).then((res) => {
        this.subscribed = res.ok;            // inspectable on the element when testing
        if (!res.ok) {
          this.subscribeError = res.status;
          return res.text().then((t) => {
            // Loud in the console, silent for her: the Shopify sync is still a backstop
            // and a failed newsletter signup must never block her results.
            if (window.console && console.warn) {
              console.warn('[fit-quiz] Klaviyo subscribe failed', res.status, String(t).slice(0, 300));
            }
          }).catch(() => {});
        }
      }).catch((e) => {
        this.subscribeError = String(e && e.message || e);
        if (window.console && console.warn) console.warn('[fit-quiz] Klaviyo subscribe error', e);
      });
    }

    klaviyoIdentify(email) {
      this.klaviyoSubscribe(email);
      const k = this.klaviyo();
      if (!k) return;
      try {
        const r = k.identify(Object.assign({ $email: email }, this.quizProfile()));
        if (r && r.catch) r.catch(() => {});
      } catch (e) { /* never let analytics break the flow */ }
    }

    // The results event is what a Klaviyo flow triggers on, and it carries the matches
    // so the email can render them without us storing a results page anywhere.
    klaviyoResults(top) {
      const k = this.klaviyo();
      if (!k || this.sentResults || !this.email) return;
      this.sentResults = true;
      const maxScore = top.length ? top[0].score : 1;
      const items = top.map((r, i) => {
        const prods = this.productFor(r.sn);
        const size = this.sizeFor(prods);
        const picked = this.variantFor(prods, size);
        // Prefer the product viewResults actually hydrated and put on the card. Resolving
        // it a second time here is not guaranteed to land on the same colourway — the
        // groups are rebuilt when later catalogue pages merge — and the one that was NOT
        // hydrated has no image, which reaches the customer as a broken image in the
        // results email with no way to recover it.
        const prod = (this._shownProds && this._shownProds[i]) || picked.prod;
        const variant = picked.variant;
        return {
          Rank: i + 1,
          // With nothing answered every score ties at zero, so there is no percentage to
          // report: omit the field rather than send NaN, which serialises to null.
          ...(maxScore > 0 ? { MatchPercent: Math.max(1, Math.round(r.score / maxScore * 100)) } : {}),
          Title: prod.title,
          // ?fq=em<rank> lands in Shopify's order.landing_site and GA4's page_location, so an
          // order that arrived from the email can be told apart and tied to the rank clicked.
          // Deliberately NOT a utm_ param: Klaviyo's own link tracking owns those, and a second
          // utm_source on the same URL is a conflict we cannot test from here.
          URL: location.origin + prod.url + '?fq=em' + (i + 1),
          // Omit rather than send an empty string. An empty src renders as a broken-image
          // icon in the inbox; an absent field lets the template hide the block. Shopify
          // hands back protocol-relative urls, which a mail client cannot resolve, so the
          // leading // becomes https:// — see tests/size-calculator/run_hydrate.js.
          ...(prod.img ? { ImageURL: String(prod.img).replace(/^\/\//, 'https://') } : {}),
          Price: prod.price,
          Size: variant ? variant.size : (size || ''),
          StyleNumber: r.sn,
          InStock: !!variant,
        };
      });
      const metric = this.dataset.klaviyoMetric || 'Completed Bra Style Guide';
      // Say what these products ARE. When she answered nothing they are the best sellers,
      // not her matches — the results page says so, but the email is a separate surface
      // and would otherwise present the same three as a personal recommendation. The flow
      // branches on Basis; MatchPercent being absent is too implicit to build copy on.
      const basis = this.sel.size > 0 ? 'match' : 'popularity';
      // The welcome code runs 30 days from the day she finishes (Alexander, 2026-09-16).
      // Computed here and sent as a property rather than dated in the Klaviyo template:
      // the template would have to do date arithmetic in four accounts and get the
      // locale right in each, and a fixed date in a template silently decays — someone
      // who takes the quiz the day before it lapses gets an offer that is already dead.
      // ISO for logic, a formatted string for display, so the template only prints.
      const exp = new Date(Date.now() + 30 * 864e5);
      const iso = exp.toISOString().slice(0, 10);
      let expText = iso;
      try {
        expText = exp.toLocaleDateString(document.documentElement.lang || undefined,
          { day: 'numeric', month: 'long', year: 'numeric' });
      } catch (e) { /* older browsers: the ISO date is still correct, just plainer */ }
      const props = Object.assign({ QuizRunId: this.runId,
        Items: items, ItemCount: items.length, Basis: basis,
        CouponCode: this.dataset.couponCode || 'WELCOME10',
        CouponDays: 30, CouponExpires: iso, CouponExpiresText: expText,
      }, this.quizProfile());

      // Post the event ourselves rather than through klaviyo.track().
      //
      // The onsite SDK refuses to send events whenever tracking is switched off — it logs
      // "Klaviyo tracking is disabled ... set the cookie __kla_off to false" and track()
      // becomes a silent no-op. identify() and our own subscribe still work, so a profile
      // would appear, subscribed, with every FitQuiz property set — and NO event. The flow
      // triggers on the event, so those customers never receive their results.
      //
      // The client endpoint is the same public, consent-carrying path we use to subscribe,
      // and it attaches the event to the identified profile by email.
      this.klaviyoEvent(metric, props);

      // Belt and braces: if the SDK is willing, it also fires. Klaviyo de-duplicates on
      // its own side far better than we can guess at from here.
      try {
        const r = k.track(metric, props);
        if (r && r.catch) r.catch(() => {});
      } catch (e) { /* never let analytics break the flow */ }
    }

    klaviyoEvent(metric, props) {
      if (this.qa) return Promise.resolve();
      const company = this.klaviyoCompanyId();
      if (!company || !this.email) return Promise.resolve();
      const body = {
        data: {
          type: 'event',
          attributes: {
            properties: props,
            metric: { data: { type: 'metric', attributes: { name: metric } } },
            profile: { data: { type: 'profile', attributes: { email: this.email } } }
          }
        }
      };
      return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify(body),
        keepalive: true
      }).then((res) => {
        this.eventSent = res.ok;
        if (!res.ok) {
          return res.text().then((t) => {
            if (window.console && console.warn) {
              console.warn('[fit-quiz] Klaviyo event failed', res.status, String(t).slice(0, 300));
            }
          }).catch(() => {});
        }
      }).catch((e) => {
        this.eventError = String(e && e.message || e);
        if (window.console && console.warn) console.warn('[fit-quiz] Klaviyo event error', e);
      });
    }

    // ---- scoring ------------------------------------------------------------------
    // Products grouped by TITLE, not style_number (D-031). Titles are 1:1 with styles,
    // whereas ten style numbers cover both a single and its 2-Pack — which is how
    // multipacks kept surfacing — and ten products carry no style_number at all.
    groups() {
      if (this._groups) return this._groups;
      const g = new Map();
      (this.products.bras || []).forEach(p => {
        const k = p.key || p.style || p.handle;
        if (!g.has(k)) g.set(k, []);
        g.get(k).push(p);
      });
      this._groups = g;
      return g;
    }

    // Attributes are derived per product at render time; a group's attributes are the
    // union across its colourways, since they are the same garment.
    attrsFor(key) {
      const set = new Set();
      (this.groups().get(key) || []).forEach(p => (p.attrs || []).forEach(a => set.add(a)));
      return set;
    }

    // The legacy matrix is now consulted only for the Yotpo fit line, which is keyed
    // on style_number. Scoring comes from the live attributes.
    styleRow(key) {
      const p = (this.groups().get(key) || [])[0];
      return (p && p.style && this.D.styles[p.style]) || null;
    }

    rank() {
      const d = {};
      this.D.questions.forEach(q => q.answers.forEach(a => {
        if (this.sel.has(a.id)) for (const k in a.w) d[k] = (d[k] || 0) + a.w[k] * q.mult;
      }));
      const out = [];
      this.groups().forEach((prods, key) => {
        let s = 0;
        this.attrsFor(key).forEach(k => { s += (d[k] || 0); });
        const st = this.styleRow(key);
        out.push({ sn: key, st: st || { attrs: {}, fit: null }, score: s });
      });
      return out.sort((a, b) => (b.score - a.score)
        || ((b.st.fit && b.st.fit.reviews) || 0) - ((a.st.fit && a.st.fit.reviews) || 0));
    }

    productFor(key) { return this.groups().get(key) || []; }

    // Active + online-store publication is already guaranteed: the section iterates
    // collection.products, which never yields a draft, archived or unpublished product.
    // Stock is not — a style whose every colourway is sold out would otherwise still
    // rank. Recommending something nobody can buy is the same broken promise as
    // recommending a bad fit, so it drops out of the ranking entirely.
    hasStock(key) {
      return this.productFor(key).some(p => this.sellable(p.variants).length > 0);
    }

    // Variants come back in the order they were created, so the chips read
    // "70A, 70B, 75A, 70C". Sort band-then-cup for band/cup sizing, and by the standard
    // run for alpha sizing, so the row scans like the PDP's own size picker.
    sortSizes(list) {
      const ALPHA = ['XXS','XS','S','S+','M','M+','L','L+','XL','XL+','XXL','XXXL'];
      const key = (v) => {
        const t = String(v.size || '').toUpperCase().trim();
        const m = t.match(/^(\d+)\s*([A-Z+]*)$/);
        if (m) return [0, Number(m[1]), m[2]];
        const i = ALPHA.indexOf(t);
        return [1, i === -1 ? 999 : i, t];
      };
      return list.slice().sort((a, b) => {
        const x = key(a), y = key(b);
        return (x[0] - y[0]) || (x[1] - y[1]) || String(x[2]).localeCompare(String(y[2]));
      });
    }

    // Which sizing system a product sells in is read off its actual variant values
    // ("75C" vs "M") — the catalogue's wire flag is low-confidence for this.
    sizeFor(prods) {
      if (!this.sizes || !prods.length) return null;
      const bandcupLike = prods[0].variants.some(v => /^\d+[A-Z]/.test(v.size));
      return bandcupLike ? this.sizes.bandcup : this.sizes.alpha;
    }

    // + sizes (S+/M+/L+) are deliberately never recommended (Alexander, 2026-09-14).
    // They are a different fit intent, and the alpha chart has no measurements for them,
    // so we cannot honestly work out who should be in one. They stay buyable on the PDP.
    sellable(variants) {
      return (variants || []).filter(v => v.avail && String(v.size || '').indexOf('+') === -1);
    }

    variantFor(prods, size) {
      if (!size) return { prod: prods[0], variant: null };
      for (const p of prods) {
        const v = this.sellable(p.variants).find(v => v.size.toUpperCase() === size.toUpperCase());
        if (v) return { prod: p, variant: v };
      }
      const p0 = prods[0];
      return { prod: p0, variant: null };
    }

    // ---- results ------------------------------------------------------------------
    // The card is a small product view: swipeable gallery like the PLP, and the colour
    // dots swap the images and the add-to-cart variant in place rather than navigating
    // away. Colourways are separate products at OB, so the sibling is looked up in the
    // pool we already rendered; if it is not there, the dot falls back to a link.
    buildCard(r, label, extraClass, rank) {
      const prods = this.productFor(r.sn);
      const size = this.sizeFor(prods);
      const start = this.variantFor(prods, size);
      const fl = fitLine(r.st.fit, this.D.fit_thresholds, (k, d) => this.T(k, d));
      const card = document.createElement('div');
      card.className = 'fq__card' + (extraClass ? ' ' + extraClass : '');
      card.innerHTML = `
        <div class="fq__card-media">
          <div class="fq__gallery" data-gallery></div>
          ${label ? `<span class="fq__badge">${label}</span>` : ''}
          <div class="fq__dots" data-dots></div>
        </div>
        <div class="fq__card-body">
          <p class="fq__pname" data-name></p>
          <span class="fq__price" data-price></span>
          <div class="fq__card-row">
            <div class="fq__colors" data-colors></div>
            <span class="fq__sizebox" data-sizebox title="Your size"></span>
          </div>
          <p class="fq__fitline" data-avail hidden></p>
          <div class="fq__sizes" data-sizes role="group" aria-label="${this.T('card_choose_size', 'Choose a size')}" hidden></div>
          ${fl ? `<p class="fq__fitline">${fl}</p>` : ''}
          <div class="fq__card-actions">
            <button class="fq__cta fq__cta--add" data-add></button>
            <a class="fq__wish" data-wish aria-label="View this product">
              <svg viewBox="0 0 27 24" aria-hidden="true"><path d="M13.5 22.5C13.5 22.5 1.5 15.9 1.5 8.2 1.5 4.8 4.2 2 7.6 2c2.3 0 4.5 1.3 5.9 3.4C14.9 3.3 17.1 2 19.4 2 22.8 2 25.5 4.8 25.5 8.2c0 7.7-12 14.3-12 14.3z" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
            </a>
          </div>
        </div>`;

      const gal = card.querySelector('[data-gallery]');
      const dots = card.querySelector('[data-dots]');
      let cur = start.prod, curVar = start.variant;
      const goTo = (k) => gal.scrollTo({ left: k * gal.clientWidth, behavior: 'smooth' });
      const index = () => (gal.clientWidth ? Math.round(gal.scrollLeft / gal.clientWidth) : 0);

      const paint = () => {
        const imgs = (cur.images && cur.images.length) ? cur.images : (cur.img ? [cur.img] : []);
        // the image is a link to the product; a swipe does not fire a click
        const href = this.pdpUrl(cur, curVar);
        gal.innerHTML = imgs.map(u => `<a class="fq__shot" href="${href}" tabindex="-1" aria-label="${cur.title}"><img src="${u}" alt="${cur.title}" loading="lazy"></a>`).join('');
        dots.innerHTML = imgs.length > 1
          ? imgs.map((_, k) => `<button type="button" class="${k ? '' : 'is-on'}" data-go="${k}" aria-label="Image ${k + 1}"></button>`).join('')
          : '';
        gal.scrollLeft = 0;
        card.querySelector('[data-name]').textContent = cur.title;
        card.querySelector('[data-price]').textContent = cur.price;
        // the heart is wired once, below — repainting must not clobber it into a link
        const sb = card.querySelector('[data-sizebox]');
        sb.textContent = (size && curVar) ? curVar.size : '';
        sb.hidden = !(size && curVar);
        // No size for her — because she skipped measuring, or this colourway is not made
        // in hers — so let her pick it here, with the PDP's own size chips, rather than
        // sending her away. Only the genuinely unbuyable case still gets a message.
        const av = card.querySelector('[data-avail]');
        const row = card.querySelector('[data-sizes]');
        const avail = this.sellable(cur.variants);
        if (size && curVar) {
          // we worked her size out and this colourway has it
          av.hidden = true; av.innerHTML = '';
          row.hidden = true; row.innerHTML = '';
        } else if (avail.length) {
          // she skipped measuring, or this colourway is not made in her size: let her
          // pick here. The row stays up after she picks so she can change her mind.
          av.hidden = true; av.innerHTML = '';
          row.hidden = false;
          row.innerHTML = this.sortSizes(avail).map(v =>
            `<button type="button" class="fq__size-opt" data-vid="${v.id}"
               aria-pressed="${curVar && curVar.id === v.id}">${v.size}</button>`
          ).join('');
        } else {
          row.hidden = true; row.innerHTML = '';
          av.hidden = false;
          av.innerHTML = size ? `Not available in <b>${size}</b> in this colour` : this.T('card_sold_out', 'Sold out');
        }
        const add = card.querySelector('[data-add]');
        // Match the "Why not make it a set?" card exactly (Alexander, 2026-09-16). When a
        // size is pickable the button is always "Add to cart", disabled until she picks
        // one — it does NOT turn into "See product" and send her to the PDP to do the same
        // thing again. "See product" survives only where adding is genuinely impossible,
        // i.e. no sellable variant in this colourway at all.
        const canPick = avail.length > 0;
        add.disabled = canPick && !curVar;
        add.textContent = (curVar || canPick)
          ? this.T('card_add', 'Add to cart')
          : this.T('card_see', 'See product');
        card.querySelector('[data-colors]').innerHTML = this.swatchesFor(cur);
      };
      paint();

      gal.addEventListener('scroll', () => {
        if (!dots.children.length) return;
        const k = index();
        [...dots.children].forEach((d, n) => d.classList.toggle('is-on', n === k));
      }, { passive: true });
      dots.addEventListener('click', (e) => {
        const b = e.target.closest('[data-go]');
        if (b) goTo(+b.dataset.go);
      });

      this.bindWish(card.querySelector('[data-wish]'), cur);

      card.querySelector('[data-sizes]').addEventListener('click', (e) => {
        const btn = e.target.closest('[data-vid]');
        if (!btn) return;
        curVar = (cur.variants || []).find(v => String(v.id) === btn.dataset.vid) || null;
        paint();
      });

      card.querySelector('[data-colors]').addEventListener('click', (e) => {
        const sw = e.target.closest('[data-color-url]');
        if (!sw || sw.getAttribute('aria-pressed') === 'true') return;
        const keep = this.colorsFor(cur);
        sw.classList.add('is-loading');
        this.colorProduct(sw.dataset.colorUrl, keep).then(sib => {
          cur = { ...sib, colors: sib.colors && sib.colors.length ? sib.colors : keep };
          curVar = size ? this.sellable(cur.variants).find(v => v.size.toUpperCase() === size.toUpperCase()) || null : null;
          paint();
        }).catch(() => { window.location.href = sw.dataset.colorUrl; });
      });

      card.querySelector('[data-add]').addEventListener('click', (e) => {
        const btn = e.currentTarget;
        if (curVar) { this.addToCart(btn, curVar.id, cur, { src: 'results', rank: rank, style: r.sn }); return; }
        // Disabled while a size is pickable, so reaching here means there is nothing to
        // add in this colourway — the PDP is the only useful destination left.
        if (!btn.disabled) window.location.href = this.pdpUrl(cur, curVar);
      });
      // she is leaving for the product page: the click-through the email also measures
      card.addEventListener('click', (e) => {
        const a = e.target.closest && e.target.closest('a[href]');
        if (a && !a.hasAttribute('data-wish')) this.track('fit_quiz_result_click', { fq_rank: rank, fq_style: r.sn });
      });
      return card;
    }

    // Desktop presents the guide as a modal. Guarded because matchMedia is absent in
    // jsdom (and in very old browsers), where an unguarded call threw on every click.
    // Copy lookup. Strings live in locales/*.json under `fit_quiz` and are handed to the
    // element as a JSON blob by the section; the English text stays here as the fallback
    // so a missing key degrades to readable copy rather than a blank button.
    T(key, fallback) {
      if (!this._i18n) {
        var el = this.querySelector('[data-fq-i18n]') || document.querySelector('[data-fq-i18n]');
        try { this._i18n = el ? JSON.parse(el.textContent) : {}; } catch (e) { this._i18n = {}; }
      }
      var v = this._i18n[key];
      return (typeof v === 'string' && v.length) ? v : fallback;
    }

    isModal() {
      // Embedded in the PDP's Size Guide drawer, the drawer owns the frame, the scrim and
      // the close button — so none of the page's own modal behaviour applies. Without this
      // the document-level scrim handler treated a click on "Back to size chart" (which
      // sits above the quiz element) as a click outside the panel and navigated away.
      if (this.classList.contains('fq--embedded')) return false;
      return !!(window.matchMedia && window.matchMedia('(min-width: 990px)').matches);
    }

    // A recommendation already knows which variant fits, so the PDP opens on that size
    // rather than making her pick it again.
    //
    // BOTH parameters are needed, and it is ?size= that does the visible work: Shopify's
    // own ?variant= sets the form's hidden id but leaves this theme's band/cup picker
    // blank with the CTA reading "Select a size" (verified on the live PDP). ?size= is
    // what the theme reads to preselect, and it already preserves it across colour
    // changes. ?variant= is kept so price and stock resolve on first paint.
    //
    // Falls back to the plain product URL when no variant resolved (size out of range, or
    // that colourway sold out in her size) — never guess a size on her behalf.
    pdpUrl(prod, variant) {
      const base = (prod && prod.url) || '';
      if (!base || !variant) return base;
      const j = base.indexOf('?') > -1 ? '&' : '?';
      return base + j + 'variant=' + variant.id + '&size=' + encodeURIComponent(variant.size);
    }

    braByUrl(url) {
      const h = String(url || '').split('?')[0].split('/').filter(Boolean).pop();
      return this.products.bras.find(p => p.handle === h) || null;
    }

    // Colourways are separate products at OB and most of them are not in the collection
    // we render (49 products, ~8 colours each), so the pool only ever holds a couple of
    // them. Fall back to the product's own .js so a swatch always resolves.
    colorProduct(url, fallbackColors) {
      const clean = String(url || '').split('?')[0].replace(/\/$/, '');
      const local = this.braByUrl(clean);
      if (local) return Promise.resolve(local);
      this._colorCache = this._colorCache || {};
      if (this._colorCache[clean]) return Promise.resolve(this._colorCache[clean]);
      return fetch(clean + '.js').then(r => { if (!r.ok) throw 0; return r.json(); }).then(d => {
        const names = (d.options || []).map(o => (typeof o === 'string' ? o : o.name));
        const si = names.findIndex(n => /^size$/i.test(n));
        const ci = names.findIndex(n => /^colou?r$/i.test(n));
        const p = {
          handle: d.handle,
          title: d.title,
          url: clean,
          priceCents: d.price,
          price: this.money(d.price),
          // images[] is gallery order and does not always lead with the hero shot
          // (some styles open on the matching brief) — put featured_image first.
          img: d.featured_image || (d.images || [])[0] || '',
          images: [d.featured_image].concat(d.images || [])
            .filter((u, i, a) => u && a.indexOf(u) === i).slice(0, 4),
          color: ci >= 0 && d.variants[0] ? d.variants[0].options[ci] : null,
          colors: fallbackColors || [],
          variants: (d.variants || []).map(v => ({
            id: v.id, size: si >= 0 ? v.options[si] : v.title, avail: v.available,
          })),
        };
        this._colorCache[clean] = p;
        return p;
      });
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency', currency: this.dataset.currency || 'DKK',
        }).format(cents / 100);
      } catch (e) { return (cents / 100).toFixed(2); }
    }

    // Multipacks are out of scope for the quiz. The style number does not reliably
    // carry "PACK" (e.g. the Core Wireless Plunge 2-Pack), so check the titles too.
    isMultipack(key) {
      if (/pack/i.test(key)) return true;
      return this.productFor(key).some(p => /\b\d+[- ]?pack\b/i.test(p.title || ''));
    }

    // Colour swatches come from the PDP's own connected-products list; the hex for a
    // colour name comes from the shop's color_list metaobject (the same source the
    // theme's own swatches use). No data -> no swatches, just the size box.
    colorHex(name) {
      if (!this._hex) {
        this._hex = {};
        const list = (this.products && this.products.colorList && this.products.colorList.colors) || [];
        list.forEach(c => { if (c && c.name) this._hex[String(c.name).toLowerCase()] = c; });
      }
      const k = String(name || '').toLowerCase();
      // two-tone names ("Black / Hazelnut") are not in the colour list; fall back to
      // the first colour in the name so the swatch still shows something truthful
      return this._hex[k] || this._hex[k.split('/')[0].trim()] || null;
    }

    // The section emits `colors` once per style (they are identical across a style's
    // colourways), so fall back to the style's first product that carries the list.
    colorsFor(prod) {
      if (prod && prod.colors && prod.colors.length) return prod.colors;
      const key = prod && (prod.key || prod.handle);
      const group = key ? (this.groups().get(key) || []) : [];
      const withColors = group.find(p => p.colors && p.colors.length);
      return (withColors && withColors.colors) || [];
    }

    swatchesFor(prod) {
      const cols = this.colorsFor(prod);
      if (!cols.length) return '';
      // every colourway is shown — nothing hidden behind a "+N". Only a colour we can
      // actually paint gets a swatch; an empty box would be worse than none.
      return cols.filter(c => this.colorHex(c.name)).map(c => {
        const meta = this.colorHex(c.name);
        const bg = meta.image ? `background-image:url(${meta.image})` : `background-color:${meta.hex}`;
        const cur = c.name === prod.color ? ' fq__swatch--on' : '';
        return `<button type="button" class="fq__swatch${cur}" data-color-url="${c.url}" title="${c.name}" aria-label="${c.name}" aria-pressed="${c.name === prod.color}"><span style="${bg}"></span></button>`;
      }).join('');
    }

    // NOT RENDERED. The "Best for X" strip was dropped from the results page — the V2
    // answers already weight the main ranking, so a second list said the same thing
    // twice. Kept because it is the only place that scores a single answer in isolation.
    featurePicks(taken) {
      const q = this.D.questions.find(x => x.id === 'V2');
      if (!q) return [];
      const chosen = q.answers.filter(a => this.sel.has(a.id));
      const picks = [];
      chosen.slice(0, 2).forEach(a => {
        const ranked = Object.entries(this.D.styles)
          .map(([sn, st]) => {
            let sc = 0; for (const k in a.w) { if (st.attrs[k]) sc += a.w[k]; }
            return { sn, st, score: sc };
          })
          .filter(r => r.score > 0 && !taken.has(r.sn) && !this.isMultipack(r.sn) && this.productFor(r.sn).length && this.passesFabric(r.sn))
          .sort((x, y) => (y.score - x.score) || (((y.st.fit && y.st.fit.reviews) || 0) - ((x.st.fit && x.st.fit.reviews) || 0)));
        if (ranked.length) {
          taken.add(ranked[0].sn);
          picks.push({ ...ranked[0], feature: a.label });
        }
      });
      return picks;
    }

    viewResults(view) {
      // Ranking before the rest of the catalogue has landed would quietly score against
      // a partial range. It is normally merged long before anyone finishes the quiz;
      // if not, say so and re-render rather than recommend from half the products.
      if (!this.pagesDone) {
        view.innerHTML = `
          <h2 class="fq__title">${this.T('results_loading_1', 'Finding your')}<span>${this.T('results_loading_2', 'best matches\u2026')}</span></h2>
          <div class="fq__loading" role="status" aria-live="polite"><span></span><span></span><span></span></div>`;
        this.pagesReady.then(() => {
          if (this.steps[this.i] === 'results') this.show();
        });
        return;
      }
      // Some of the range never arrived, even after retries. Ranking now would quietly
      // score against a hole: a style whose only in-stock colourway sat on the lost page
      // reads as unavailable and is dropped, so she would get a confident, badged answer
      // built from a catalogue we know is incomplete. Say so and offer to try again —
      // the same principle as the empty state, applied to a range we could not load
      // rather than a range that had no match.
      if (this.pagesComplete === false) {
      if (this._catErr !== this.runId) { this._catErr = this.runId; this.track('fit_quiz_error', { fq_stage: 'catalogue', fq_message: 'pages lost: ' + (this.pagesLost || []).join(',') }); }
        view.innerHTML = `
          <h2 class="fq__title">${this.T('results_partial_1', 'We could not load')}<span>${this.T('results_partial_2', 'the full range')}</span></h2>
          <div class="fq__empty"><p>${this.T('results_partial_body', 'Part of the bra range did not load, so we would rather not guess. Your answers are saved \u2014 try again.')}</p></div>
          <div class="fq__nav"><button type="button" class="fq__cta" data-reload>${this.T('results_partial_cta', 'Try again')}</button></div>`;
        const again = view.querySelector('[data-reload]');
        if (again) again.addEventListener('click', () => {
          again.disabled = true;
          again.textContent = this.T('results_partial_retrying', 'Loading\u2026');
          this.pagesDone = false;
          this.pagesComplete = undefined;
          this.loadRestOfCatalogue();
          this.pagesReady.then(() => { if (this.steps[this.i] === 'results') this.show(); });
        });
        this.stage.innerHTML = '';
        this.stage.appendChild(view);
        return;
      }
      // Every question is skippable (2026-09-16), so she can arrive having answered
      // nothing. "No confident match" is the right answer when her answers matched
      // nothing, but a dead end when she has no answers to change: fall back to what
      // customers rate highest. rank() already breaks ties on review count, and with
      // nothing selected every score ties at zero — so this is review order.
      const answered = this.sel.size > 0;
      const eligible = this.rank().filter(r => this.productFor(r.sn).length && this.hasStock(r.sn) && !this.isMultipack(r.sn) && this.passesFabric(r.sn));
      // She answered nothing, so there is no match to rank on. Order by what actually
      // sells instead of by review count: reviews measure who bothered to write one,
      // which is not the same as popular. `sold` is 12 months of units on this store,
      // baked into fit-quiz-data.json. Anything without a figure sorts last rather than
      // first, so a missing number can never win the page.
      if (!answered) eligible.sort((a, b) => ((b.st && b.st.sold) || 0) - ((a.st && a.st.sold) || 0));
      const ranked = answered ? eligible.filter(r => r.score > 0) : eligible;
      const top = ranked.slice(0, 3);
      // Fetch the images for the three cards before painting, behind the same spinner
      // this step already shows while the later catalogue pages merge.
      const shown = top.map(r => {
        const prods = this.productFor(r.sn);
        return this.variantFor(prods, this.sizeFor(prods)).prod;
      }).filter(Boolean);
      if (shown.some(p => !p.img && !p._imgDone)) {
        view.innerHTML = `
          <h2 class="fq__title">${this.T('results_loading_1', 'Finding your')}<span>${this.T('results_loading_2', 'best matches\u2026')}</span></h2>
          <div class="fq__loading" role="status" aria-live="polite"><span></span><span></span><span></span></div>`;
        this.hydrateCardImages(shown).then(() => {
          if (this.steps[this.i] === 'results') this.show();
        });
        return;
      }
      // Hand the resolved, hydrated products to the Klaviyo event so it reports the same
      // colourway the card shows rather than resolving its own.
      this._shownProds = shown;
      const sz = this.sizes || {};
      const sizeLabel = [sz.bandcup, sz.alpha].filter(Boolean).join(' / ');
      view.innerHTML = `
        <h2 class="fq__title">${this.T('results_title_1', 'Made for you.')}<span>${this.T('results_title_2', 'Your best matches!')}</span></h2>
        ${sizeLabel ? `<div class="fq__sizecard"><span class="fq__sizecard-label">${this.T('results_size_label', 'Your size')}</span><p class="fq__sizecard-value">${sizeLabel}</p></div>` : ''}
        ${top.length ? (answered ? '' : `<p class="fq__hint">${this.T('results_unanswered', 'You skipped the questions, so these are our best-selling bras rather than a personal match.')}</p>`) : `<div class="fq__empty"><p><b>${this.T('results_empty_strong', 'No confident match.')}</b> Nothing in the range clearly fits what you picked — we'd rather show you nothing than a bad match. Try changing an answer, or browse the full bra collection.</p></div>`}
        <div class="fq__cards"></div>
        <section class="fq__doubt">
          <h2 class="fq__title">${this.T('results_doubt', 'Still in doubt?')}</h2>
          <button type="button" class="fq__cta fq__cta--wide" data-retake>${this.T('results_retake', 'Retake quiz')}</button>
          <a class="fq__cta fq__cta--wide" href="${this.dataset.supportUrl || '/pages/contact'}">${this.T('results_support', 'Contact our Customer Support')}</a>
        </section>`;
      this.trackComplete(top, answered, sizeLabel);
      this.klaviyoResults(top);
      const wrap = view.querySelector('.fq__cards');
      const maxScore = top.length ? top[0].score : 1;
      top.forEach((r, i) => wrap.appendChild(this.buildCard(r, answered ? `${Math.max(1, Math.round(r.score / maxScore * 100))}% match` : '', i === 0 ? 'fq__card--top' : '', i + 1)));
      const retake = view.querySelector('[data-retake]');
      if (retake) retake.addEventListener('click', () => {
        this.sel = new Set(); this.sizes = null; this.sizeSkipped = false; this.ubVal = this.bustVal = '';
        this.addedBra = null; this.i = 0;
        // a retake is a new attempt: new run id, and every once-per-run guard resets
        this.runId = this.newRun(); this._trackedStep = null; this._startSent = false;
        this.track('fit_quiz_retake', {});
        this.show(true);
      });
    }

    // The theme's <cart-drawer> renders itself from the sections the add returns, then
    // opens itself — same contract its own add-to-cart buttons use. Falling back to
    // /cart only if the drawer is not on the page.
    openCart(res) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && res && res.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents(res);
        return true;
      }
      if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
      return false;
    }

    // The add already asks for the cart-drawer and cart-icon-bubble sections back; it
    // just never used them, so the bag kept its old count and the drawer still said
    // "Your cart is empty" while the item sat in the cart server-side. This swaps both
    // in WITHOUT opening the drawer — openCart() is still what opens it on request.
    // Same DOM contract the theme's own renderContents uses.
    syncCart(res) {
      if (!res || !res.sections) return;
      const parse = (html, sel) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return (sel && doc.querySelector(sel)) || doc.body;
      };
      const bubbleHtml = res.sections['cart-icon-bubble'];
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubbleEl) bubbleEl.innerHTML = parse(bubbleHtml).innerHTML;

      const drawerHtml = res.sections['cart-drawer'];
      const drawerEl = document.querySelector('cart-drawer');
      if (drawerHtml && drawerEl) {
        const src = parse(drawerHtml, 'cart-drawer');
        if (src) {
          drawerEl.innerHTML = src.innerHTML;
          // the empty state is a class on the element, not part of its contents
          drawerEl.classList.toggle('is-empty', src.classList.contains('is-empty'));
        }
      }
    }

    addToCart(btn, idOrItems, prod, meta) {
      const props = meta ? this.cartProps(meta) : null;
      const items = (Array.isArray(idOrItems) ? idOrItems : [{ id: idOrItems, quantity: 1 }])
        .map(it => (props && !it.properties ? Object.assign({}, it, { properties: props }) : it));
      btn.disabled = true; btn.textContent = this.T('card_adding', 'Adding\u2026');
      let res = null;
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname + window.location.search,
        }),
      }).then(r => { if (!r.ok) throw 0; return r.json(); }).then((d) => { res = d; return d; }).then(() => {
        this.lastCart = res;
        this.addedBra = prod;
        this.syncCart(res);
        if (meta) this.track('fit_quiz_add_to_cart', { fq_src: meta.src, fq_rank: meta.rank || '', fq_style: meta.style || '' });
        this.toast('Added to cart');
        // the matching bottoms are their own screen again, shown after the add
        this.hydrateStyledWith(prod).then(() => {
          if (this.upsellPicks(prod).length) {
            if (!this.steps.includes('upsell')) this.steps.push('upsell');
            this.i = this.steps.indexOf('upsell');
            this.show();
          } else {
            btn.textContent = this.T('card_added', 'Added \u2713');
          }
        });
      }).catch(() => { btn.disabled = false; btn.textContent = this.T('card_try', 'Try again'); });
    }

    // ---- upsell -------------------------------------------------------------------
    // The PDP's own "Complete the look" list (custom.upsell_styled_with) is the curated,
    // merchandised match for this bra — always prefer it. Only when a bra has none do we
    // fall back to matching real underwear out of the bottoms collection.
    // styledWith now arrives as handles (see the section). Hydrate them from the same
    // /products/<handle>.js endpoint the colour swatches use, once, when she adds a bra.
    // Bra images are no longer inlined for all ~250 colourways (see the section): that
    // cost 1,250 image_url calls per render to serve three cards, and it is what tipped
    // the section over Shopify's Liquid budget. Pull them for the products we are about
    // to show, from the same /products/<handle>.js the swatches and styledWith use.
    //
    // This runs BEFORE the cards and before klaviyoResults, not lazily inside the card,
    // because the results event carries ImageURL into the email — a card that fills in a
    // moment later is fine, an email with an empty image is not.
    hydrateCardImages(prods) {
      const need = prods.filter(p => p && !p.img && !p._imgDone);
      if (!need.length) return Promise.resolve();
      return Promise.all(need.map(p => {
        p._imgDone = true;                       // one attempt each, success or not
        return fetch('/products/' + p.handle + '.js', { credentials: 'same-origin' })
          .then(r => (r.ok ? r.json() : null))
          .then(d => {
            if (!d) return;
            p.img = d.featured_image || (d.images || [])[0] || '';
            p.images = [d.featured_image].concat(d.images || [])
              .filter((u, i, a) => u && a.indexOf(u) === i).slice(0, 4);
          })
          // A missing image must not lose the card — but it DID reach a customer as a
          // blank frame in the results email once, and a silent catch is why it took a
          // screenshot to find. Say so in the console so the next one is diagnosable.
          .catch(() => {
            if (window.console && console.warn) {
              console.warn('[fit-quiz] no image for ' + p.handle + ' — results email will omit it');
            }
          });
      }));
    }
    hydrateStyledWith(bra) {
      const handles = (bra && bra.styledWith) || [];
      if (!handles.length || typeof handles[0] !== 'string') return Promise.resolve();
      return Promise.all(handles.slice(0, 6).map(h =>
        fetch('/products/' + h + '.js', { credentials: 'same-origin' })
          .then(r => (r.ok ? r.json() : null))
          .then(d => {
            if (!d) return null;
            const si = (d.options || []).findIndex(o => (o.name || o) === 'Size');
            const ci = (d.options || []).findIndex(o => ['Color', 'Colour'].indexOf(o.name || o) > -1);
            return {
              handle: d.handle,
              title: d.title,
              url: '/products/' + d.handle,
              price: this.money(d.price),
              img: (d.featured_image || (d.images || [])[0] || ''),
              images: [d.featured_image].concat(d.images || [])
                .filter((u, i, a) => u && a.indexOf(u) === i).slice(0, 4),
              mat: null,
              color: ci > -1 && d.variants[0] ? d.variants[0].options[ci] : null,
              variants: (d.variants || []).map(v => ({
                id: v.id, size: si > -1 ? v.options[si] : v.title, avail: v.available
              })),
            };
          })
          .catch(() => null)
      )).then(list => { bra.styledWith = list.filter(Boolean); });
    }

    money(cents) {
      const n = (Number(cents) || 0) / 100;
      const cur = this.dataset.currency || '';
      return n.toFixed(2).replace(/\.00$/, '') + (cur ? ' ' + cur : '');
    }

    upsellPicks(bra) {
      const WANT = 4;
      const out = [];
      const seen = new Set();
      const add = (p) => { if (p && !seen.has(p.handle)) { seen.add(p.handle); out.push(p); } };
      // the merchandised pick always leads
      ((bra && bra.styledWith) || [])
        .filter(p => p && typeof p !== 'string' && p.variants.some(v => v.avail))
        .forEach(add);
      if (out.length >= WANT) return out.slice(0, WANT);
      const UW = /thong|brief|hipster|shortie|cheeky|bikini/i;
      const words = (bra && bra.title ? bra.title : '').split(/\s+/);
      const prefixLen = (t) => {
        const w = t.split(/\s+/); let n = 0;
        while (n < words.length && n < w.length && w[n].toLowerCase() === words[n].toLowerCase()) n++;
        return n;
      };
      // then fill up, preferring the colourway she is looking at, then the same
      // family name, then the same material
      const braColor = String((bra && bra.color) || '').toLowerCase();
      this.products.bottoms
        .filter(p => UW.test(p.title) && p.variants.some(v => v.avail))
        .map((p, i) => ({ p, key: [
          braColor && String(p.color || '').toLowerCase() === braColor ? 1 : 0,
          prefixLen(p.title),
          bra && p.mat === bra.mat ? 1 : 0,
          -i,
        ] }))
        .sort((a, b) => (b.key[0] - a.key[0]) || (b.key[1] - a.key[1]) || (b.key[2] - a.key[2]) || (b.key[3] - a.key[3]))
        .forEach(x => { if (out.length < WANT) add(x.p); });
      return out.slice(0, WANT);
    }

    viewUpsell(view) {
      const mat = this.addedBra && this.addedBra.mat;
      const picks = this.upsellPicks(this.addedBra);
      const curated = !!(this.addedBra && (this.addedBra.styledWith || [])
        .some(p => p && typeof p !== 'string' && p.variants.some(v => v.avail)));
      const bra = this.addedBra || {};
      const col = bra.color && picks.some(p => String(p.color || '').toLowerCase() === String(bra.color).toLowerCase())
        ? bra.color : null;
      const sub = col
        ? `Same ${col.toLowerCase()} as the ${bra.title} you just added — buy them together and they actually match.`
        : curated ? `The bottoms we cut to go with the ${bra.title}.`
        : (mat && picks.some(p => p.mat === mat) ? `In the same ${String(mat).toLowerCase()} as your bra, so they wear and wash the same.`
          : 'The bottoms that go with the bra you just added.');
      view.innerHTML = `
        <h2 class="fq__title">Why not make<span>it a set?</span></h2>
        <p class="fq__hint">${sub}</p>
        <div class="fq__upsell-grid"></div>
        <div class="fq__nav"><button type="button" class="fq__cta" data-viewcart>View cart</button></div>`;
      const vc = view.querySelector('[data-viewcart]');
      if (vc) vc.addEventListener('click', () => { if (!this.openCart(this.lastCart)) window.location.href = '/cart'; });
      const grid = view.querySelector('.fq__upsell-grid');
      // Bottoms no longer ship their images inline (see the section), so pull them for
      // the handful of cards this step actually shows — at most four, against 50 that
      // used to be rendered for everyone who never reached here. Cards are built either
      // way: hydrateCardImages marks a failed fetch and does not retry, and a pair card
      // with no image still has its name, price, sizes and Add to cart.
      const paint = () => picks.forEach(p => grid.appendChild(this.buildPairCard(p)));
      if (picks.some(p => !p.img && !p._imgDone)) {
        this.hydrateCardImages(picks).then(paint);
      } else {
        paint();
      }
    }

    // Same card as the recommendations — ratio, margins, type, buttons — with the
    // PDP's size chips in place of the fixed size box, since here she chooses.
    buildPairCard(p) {
      const sizes = this.sellable(p.variants);
      // Pre-select ONLY the size we worked out for her, and only when it is actually
      // available. The chain here used to fall back to M and then to sizes[0] — so an S
      // whose size was sold out in that colourway got XXS pre-selected with the button
      // live, and one tap put the smallest size in the range in her cart (found on live
      // DK, 2026-09-16). It bit precisely when her size was missing, which is when a
      // shopper is least likely to re-read the chips.
      //
      // Anything other than her own size is a guess, and a guess must not arrive
      // pre-selected with an enabled button. No match means nothing selected, the button
      // stays disabled, and we say why rather than leaving a dead button unexplained.
      const pref = this.sizeSkipped ? null : ((this.sizes && this.sizes.alpha) || null);
      let chosen = pref
        ? (sizes.find(v => v.size.toUpperCase() === String(pref).toUpperCase()) || null)
        : null;
      const prefMissing = !!pref && !chosen && sizes.length > 0;
      const card = document.createElement('div');
      card.className = 'fq__card';
      card.innerHTML = `
        <div class="fq__card-media">
          <div class="fq__gallery" data-gallery></div>
          <div class="fq__dots" data-dots></div>
        </div>
        <div class="fq__card-body">
          <p class="fq__pname">${p.title}</p>
          <span class="fq__price">${p.price}</span>
          ${prefMissing ? `<p class="fq__fitline" data-sizegone>${this.T('pair_size_gone', 'Your size __SIZE__ is sold out here \u2014 pick another below.').replace('__SIZE__', pref)}</p>` : ''}
          ${sizes.length ? `<div class="fq__sizes" role="group" aria-label="${this.T('card_choose_size', 'Choose a size')}">
            ${sizes.map(v => `<button type="button" class="fq__size-opt" data-vid="${v.id}"
              aria-pressed="${v === chosen}">${v.size}</button>`).join('')}
          </div>` : '<p class="fq__fitline">Sold out</p>'}
          <div class="fq__card-actions">
            <button class="fq__cta fq__cta--add" data-add ${chosen ? '' : 'disabled'}>Add to cart</button>
            <a class="fq__wish" data-wish aria-label="View ${p.title}">
              <svg viewBox="0 0 27 24" aria-hidden="true"><path d="M13.5 22.5C13.5 22.5 1.5 15.9 1.5 8.2 1.5 4.8 4.2 2 7.6 2c2.3 0 4.5 1.3 5.9 3.4C14.9 3.3 17.1 2 19.4 2 22.8 2 25.5 4.8 25.5 8.2c0 7.7-12 14.3-12 14.3z" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
            </a>
          </div>
        </div>`;
      // Same swipeable gallery as the recommendation cards (comps + Alexander 2026-09-14):
      // scroll-snap shots with dots under them, no arrows.
      const pgal = card.querySelector('[data-gallery]');
      const pdots = card.querySelector('[data-dots]');
      const paintGallery = () => {
        const imgs = (p.images && p.images.length) ? p.images : (p.img ? [p.img] : []);
        const href = this.pdpUrl(p, chosen);
        pgal.innerHTML = imgs.map(u =>
          `<a class="fq__shot" href="${href}" tabindex="-1" aria-label="${p.title}"><img src="${u}" alt="${p.title}" loading="lazy"></a>`
        ).join('');
        pdots.innerHTML = imgs.length > 1
          ? imgs.map((_, k) => `<button type="button" class="${k ? '' : 'is-on'}" data-go="${k}" aria-label="Image ${k + 1}"></button>`).join('')
          : '';
      };
      paintGallery();
      this.bindWish(card.querySelector('[data-wish]'), p);
      pgal.addEventListener('scroll', () => {
        if (!pdots.children.length) return;
        const k = pgal.clientWidth ? Math.round(pgal.scrollLeft / pgal.clientWidth) : 0;
        [...pdots.children].forEach((d, n) => d.classList.toggle('is-on', n === k));
      }, { passive: true });
      pdots.addEventListener('click', (e) => {
        const b = e.target.closest('[data-go]');
        if (b) pgal.scrollTo({ left: +b.dataset.go * pgal.clientWidth, behavior: 'smooth' });
      });

      const row = card.querySelector('.fq__sizes');
      const add = card.querySelector('[data-add]');
      if (row) row.addEventListener('click', (e) => {
        const b = e.target.closest('[data-vid]');
        if (!b) return;
        chosen = sizes.find(v => String(v.id) === b.dataset.vid) || chosen;
        [...row.children].forEach(x => x.setAttribute('aria-pressed', String(x === b)));
        // Re-enable the CTA now she has picked. Before the pre-selection fix `chosen` was
        // never null at render, so the button was never disabled and this handler never
        // had to say so — starting at null exposed that omission, and without this line a
        // card whose preferred size is sold out could not be bought at all.
        add.disabled = !chosen;
        // and drop the "your size is sold out here" line, which reads oddly next to a
        // chip she has now selected
        const gone = card.querySelector('[data-sizegone]');
        if (gone) gone.remove();
        // keep the PDP links pointing at the size she just picked
        const u = this.pdpUrl(p, chosen);
        card.querySelectorAll('.fq__shot').forEach(a => { a.href = u; });
      });
      add.addEventListener('click', () => {
        if (!chosen) return;
        add.disabled = true; add.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: chosen.id, quantity: 1, properties: this.cartProps({ src: 'pair' }) }],
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname + window.location.search }),
        }).then(r => { if (!r.ok) throw 0; return r.json(); })
          .then(d => { add.textContent = 'Added \u2713'; this.lastCart = d;
            this.track('fit_quiz_add_to_cart', { fq_src: 'pair', fq_style: p.handle });
            this.syncCart(d);
            this.openCart(d) || this.toast('Added to cart'); })
          .catch(() => { add.disabled = false; add.textContent = 'Try again'; });
      });
      return card;
    }

    // ---- measurement ---------------------------------------------------------------
    // Why this exists: on 2026-09-21 the quiz was dead for eight days (a stray </div>) and
    // nothing noticed. The quiz sent nothing until an email was submitted, so a week with zero
    // completions looked like a quiet week. These events make a drop visible the same day,
    // and make "how many started" and "where did they stop" answerable at all.
    //
    // Sent with Shopify.analytics.publish('fit_quiz_*', { fq_* ... }), Shopify's Customer Events API.
    // The first version called gtag('event'), the second dataLayer.push; neither works HERE, found
    // in review by ob-theme-perf and confirmed by probing all four stores. GTM-TZ4GJFR is not in the
    // page: a Shopify custom web pixel loads it inside a sandboxed iframe with its OWN dataLayer, so
    // the page's window.dataLayer never reaches it. (The pixel subscribes to Shopify's standard
    // events and pushes them into ITS layer, which is also why GA4 receives only a fixed set.) The
    // only way to tell a pixel about an event is publish(), and the pixel must subscribe to each
    // name — see docs/gtm-recipe-fit-quiz.md for the snippet.
    //
    // Probed on DK, UK, US and EU bra pages: publish() exists, does not throw, and Shopify.analytics
    // exposes a replayQueue, so events published before pixels finish loading are not lost. It also
    // respects the pixel's own consent, which is what we want here.
    // dataLayer is the fallback ONLY where Shopify.analytics is absent, never as well: with both,
    // any page-level GTM would double-count. Nothing
    // here may ever throw into the quiz, and a QA run (?qa=1) sends nothing.
    newRun() { return Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-5); }

    errMsg(e) { return String((e && e.message) || e || 'unknown').slice(0, 100); }

    track(name, params) {
      try {
        if (new URLSearchParams(location.search).get('qa') === '1') return;
        const p = Object.assign({
          fq_run: this.runId || '',
          fq_surface: this.classList.contains('fq--embedded') ? 'drawer' : 'page',
          fq_device: (window.matchMedia && window.matchMedia('(min-width: 990px)').matches) ? 'desktop' : 'mobile',
          fq_currency: String(this.dataset.currency || '').toLowerCase(),
        }, params || {});
        const sa = window.Shopify && window.Shopify.analytics;
        if (sa && typeof sa.publish === 'function') sa.publish(name, p);
        else (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, p));
      } catch (e) { /* never let analytics break the flow */ }
    }

    // The hidden marker that ties an order line back to a recommendation. Underscore-prefixed,
    // so cart-drawer.liquid and main-cart-items.liquid skip it (they test the first character),
    // exactly as they already do for _line_gender. Lands in raw_shopify_*.order_line_property.
    cartProps(meta) {
      const o = { _fq_run: this.runId, _fq_src: meta.src };
      if (meta.rank) o._fq_rank = String(meta.rank);
      return o;
    }

    trackComplete(top, answered, sizeLabel) {
      if (this._completeSent === this.runId) return;     // results re-render; the run completes once
      this._completeSent = this.runId;
      this.track('fit_quiz_complete', {
        fq_basis: top.length ? (answered ? 'match' : 'popularity') : 'empty',
        fq_items: top.length,
        fq_answers: this.sel.size,
        fq_size: sizeLabel ? 'yes' : 'no',
        fq_skipped_size: this.sizeSkipped ? 1 : 0,
      });
    }

    toast(msg) {
      const t = document.createElement('div');
      t.className = 'fq__toast'; t.textContent = msg;
      document.body.appendChild(t);
      setTimeout(() => t.remove(), 2200);
    }
  }

  customElements.define('fit-quiz', FitQuiz);
})();
